<template>
  <v-card class="contract-sign-view fill-height" tile>
    <!-- Toolbar Principal -->
    <v-toolbar dense color="primary" dark class="elevation-2">
      <v-icon left>mdi-account-group</v-icon>
      <span class="text-h6 font-weight-bold"
        >Control de Turnos y Contratos — RR.HH.</span
      >
      <v-spacer />

      <!-- Botón FIFO de Llamado Siguiente -->
      <v-btn
        v-if="sortedReadyList.length > 0"
        color="success"
        class="text-none font-weight-bold mr-3 elevation-3"
        rounded
        @click="callNextInQueue"
      >
        <v-icon left>mdi-bullhorn</v-icon>
        Llamar Siguiente para Firma (#1: {{ nextWorkerShortName }})
      </v-btn>

      <!-- Botón Validación de Contratos y Faltantes -->
      <v-btn
        color="amber darken-3"
        class="text-none font-weight-bold mr-3 elevation-2"
        rounded
        small
        @click="openValidationDialog"
      >
        <v-icon left small>mdi-clipboard-alert-outline</v-icon>
        Validación de Contratos
        <v-badge
          v-if="(validationSummary.missingAddress + validationSummary.missingAptitude) > 0"
          color="red"
          :content="validationSummary.missingAddress + validationSummary.missingAptitude"
          inline
          class="ml-1"
        />
      </v-btn>

      <v-btn
        text
        small
        color="white"
        class="text-none mr-2 font-weight-bold"
        to="/recursoshumanos/turnero-Terminal"
        target="_blank"
      >
        <v-icon x-small left>mdi-television-classic</v-icon>
        Pantalla TV Turnos
      </v-btn>

      <v-chip
        v-if="selectedTurn"
        color="white"
        text-color="primary"
        small
        class="font-weight-bold"
      >
        {{ selectedTurn.FullName }} ({{ selectedTurn.Dni }})
      </v-chip>
    </v-toolbar>

    <!-- BANDEJA PRINCIPAL DE COLAS Y TURNOS -->
    <v-container fluid class="pa-6">
      <v-row>
        <!-- 1. Formulario Rápido para Encolar Contrato por DNI -->
        <v-col cols="12" md="4">
          <v-card
            outlined
            class="pa-5 rounded-xl fill-height d-flex flex-column justify-space-between elevation-1 bg-white"
          >
            <div>
              <div
                class="subtitle-1 font-weight-bold primary--text mb-3 d-flex align-center"
              >
                <v-icon left color="primary">mdi-account-plus</v-icon>
                Agregar a Cola de Turnos
              </div>
              <p class="caption grey--text text--darken-1 mb-4">
                Ingrese el DNI para verificar el contrato e incorporar al
                colaborador a la cola de atención en Terminales:
              </p>

              <v-alert
                v-if="enqueueError"
                type="error"
                dense
                outlined
                class="mb-3"
              >
                {{ enqueueError }}
              </v-alert>
              <v-alert
                v-if="enqueueSuccess"
                type="success"
                dense
                outlined
                class="mb-3"
              >
                {{ enqueueSuccess }}
              </v-alert>

              <v-text-field
                v-model="inputDni"
                dense
                outlined
                maxlength="20"
                label="Número de DNI *"
                placeholder="Ej: 87654321"
                :loading="enqueueLoading"
                :disabled="enqueueLoading"
                @keyup.enter="enqueueWorker"
                prepend-inner-icon="mdi-card-account-details-outline"
              />
            </div>

            <v-btn
              color="primary"
              large
              rounded
              block
              :loading="enqueueLoading"
              :disabled="enqueueLoading || !inputDni"
              @click="enqueueWorker"
            >
              <v-icon left>mdi-tray-arrow-down</v-icon>
              ENCOLAR PARA TERMINAL
            </v-btn>
          </v-card>
        </v-col>

        <!-- 2. Monitor de Cola en Tiempo Real -->
        <v-col cols="12" md="8">
          <v-card outlined class="pa-5 rounded-xl elevation-1 bg-white">
            <div
              class="d-flex align-center justify-space-between mb-3 border-bottom pb-2 flex-wrap gap-2"
            >
              <div
                class="subtitle-1 font-weight-bold primary--text d-flex align-center"
              >
                <v-icon left color="primary">mdi-monitor-dashboard</v-icon>
                Monitor de Atención y Cola FIFO
              </div>
              <div class="d-flex align-center gap-2">
                <span class="caption grey--text mr-1"
                  >Terminales Detectados:</span
                >
                <v-chip
                  v-for="kiosk in activeKiosks"
                  :key="kiosk.KioskId"
                  x-small
                  :color="kiosk.IsBusy ? 'amber darken-2' : 'green darken-1'"
                  dark
                  class="font-weight-bold"
                >
                  <v-icon x-small left>{{
                    kiosk.IsBusy ? "mdi-account-clock" : "mdi-check-circle"
                  }}</v-icon>
                  {{ kiosk.KioskId }}: {{ kiosk.IsBusy ? "Ocupado" : "Libre" }}
                </v-chip>
                <span
                  v-if="activeKiosks.length === 0"
                  class="caption red--text font-weight-medium"
                >
                  (Sin Terminales en línea)
                </span>
              </div>
            </div>

            <v-tabs v-model="activeQueueTab" dense color="primary">
              <v-tab class="text-none font-weight-bold">
                <v-badge
                  :content="sortedReadyList.length.toString()"
                  :value="sortedReadyList.length > 0"
                  color="success"
                  overlap
                >
                  <v-icon left small color="success">mdi-check-decagram</v-icon>
                  Listos para Firma (FIFO)
                </v-badge>
              </v-tab>
              <v-tab class="text-none font-weight-bold">
                <v-badge
                  :content="activeCallsList.length.toString()"
                  :value="activeCallsList.length > 0"
                  color="amber darken-2"
                  overlap
                >
                  <v-icon left small color="amber darken-2"
                    >mdi-tablet-cellphone</v-icon
                  >
                  En Terminales
                </v-badge>
              </v-tab>
              <v-tab class="text-none font-weight-bold">
                <v-badge
                  :content="waitingList.length.toString()"
                  :value="waitingList.length > 0"
                  color="cyan darken-2"
                  overlap
                >
                  <v-icon left small color="cyan darken-2"
                    >mdi-account-clock</v-icon
                  >
                  En Espera
                </v-badge>
              </v-tab>
            </v-tabs>

            <v-tabs-items v-model="activeQueueTab" class="mt-4">
              <!-- TAB 1: LISTOS PARA ATENCIÓN (FIFO) -->
              <v-tab-item>
                <!-- Botón de acción rápida FIFO si hay gente en cola -->
                <div v-if="sortedReadyList.length > 0" class="mb-4">
                  <v-alert
                    color="success lighten-5"
                    class="border-success pa-3 rounded-lg d-flex align-center justify-space-between flex-wrap gap-2"
                  >
                    <div class="d-flex align-center">
                      <v-avatar
                        size="36"
                        color="success"
                        dark
                        class="mr-3 font-weight-bold white--text"
                      >
                        #1
                      </v-avatar>
                      <div>
                        <div class="subtitle-2 font-weight-bold text--primary d-flex align-center flex-wrap">
                          <span>Siguiente en turno: {{ sortedReadyList[0].FullName }}</span>
                          <v-chip
                            v-if="sortedReadyList[0].AssignedKiosk"
                            small
                            color="amber darken-3"
                            dark
                            class="font-weight-bold ml-2 elevation-1"
                          >
                            <v-icon x-small left>mdi-monitor-cellphone</v-icon>
                            Módulo: {{ sortedReadyList[0].AssignedKiosk }}
                          </v-chip>
                        </div>
                        <div class="caption grey--text text--darken-2 mt-1">
                          DNI: {{ sortedReadyList[0].Dni }} | Turno:
                          <strong>{{ sortedReadyList[0].TurnCode }}</strong> | Completó hace:
                          {{ timeSince(sortedReadyList[0].CompletedAt) }}
                        </div>
                      </div>
                    </div>
                    <div class="d-flex align-center gap-2">
                      <v-btn
                        v-if="sortedReadyList[0].AssignedKiosk"
                        color="orange darken-3"
                        outlined
                        rounded
                        class="font-weight-bold text-none"
                        @click="manualReleaseTurnKiosk(sortedReadyList[0])"
                        title="Liberar la tablet de este colaborador para el siguiente turno"
                      >
                        <v-icon left small>mdi-monitor-lock-open</v-icon>
                        Liberar {{ sortedReadyList[0].AssignedKiosk }}
                      </v-btn>
                      <v-btn
                        color="success"
                        rounded
                        class="font-weight-bold text-none elevation-2"
                        @click="openTurnDetails(sortedReadyList[0])"
                      >
                        <v-icon left small>mdi-draw-pen</v-icon>
                        ATENDER Y FIRMAR AHORA
                      </v-btn>
                    </div>
                  </v-alert>

                  <!-- Lista detallada ordenada por llegada -->
                  <div
                    class="caption font-weight-bold grey--text text--darken-1 mb-2"
                  >
                    Cola de atención por orden de llegada ({{
                      sortedReadyList.length
                    }}
                    pendientes):
                  </div>

                  <v-card
                    v-for="(item, idx) in sortedReadyList"
                    :key="item.TurnId || idx"
                    outlined
                    class="pa-3 mb-2 rounded-lg d-flex align-center justify-space-between flex-wrap gap-2 transition-fast"
                    :class="idx === 0 ? 'border-primary' : ''"
                  >
                    <div class="d-flex align-center">
                      <v-chip
                        small
                        :color="idx === 0 ? 'primary' : 'grey lighten-2'"
                        :text-color="idx === 0 ? 'white' : 'black'"
                        class="font-weight-bold mr-2"
                      >
                        #{{ idx + 1 }}
                      </v-chip>
                      <v-chip
                        v-if="item.AssignedKiosk"
                        small
                        color="amber darken-3"
                        dark
                        class="font-weight-bold mr-2"
                      >
                        <v-icon x-small left>mdi-monitor-cellphone</v-icon>
                        {{ item.AssignedKiosk }}
                      </v-chip>
                      <div>
                        <div class="d-flex align-center">
                          <strong class="subtitle-2 primary--text mr-2">{{
                            item.FullName
                          }}</strong>
                          <span class="caption grey--text font-mono"
                            >DNI: {{ item.Dni }}</span
                          >
                        </div>
                        <div class="caption grey--text text--darken-2">
                          Turno: <strong>{{ item.TurnCode }}</strong> | Puesto:
                          {{ item.PositionName || "General" }} | Listo a las:
                          <strong>{{
                            formatTimeOnly(item.CompletedAt)
                          }}</strong>
                        </div>
                      </div>
                    </div>

                    <div class="d-flex align-center gap-2">
                      <v-chip
                        x-small
                        color="green lighten-4"
                        class="green--text text--darken-3 font-weight-bold"
                      >
                        <v-icon x-small left color="green darken-2"
                          >mdi-clock-outline</v-icon
                        >
                        {{ timeSince(item.CompletedAt) }}
                      </v-chip>
                      <v-btn
                        v-if="item.AssignedKiosk"
                        color="orange darken-3"
                        x-small
                        outlined
                        rounded
                        class="font-weight-bold text-none"
                        @click="manualReleaseTurnKiosk(item)"
                        title="Liberar la tablet de este colaborador"
                      >
                        <v-icon x-small left>mdi-monitor-lock-open</v-icon>
                        Liberar {{ item.AssignedKiosk }}
                      </v-btn>
                      <v-btn
                        color="primary"
                        small
                        outlined
                        rounded
                        class="font-weight-bold text-none"
                        @click="openTurnDetails(item)"
                      >
                        <v-icon x-small left>mdi-draw-pen</v-icon>
                        Atender (#{{ idx + 1 }})
                      </v-btn>

                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            icon
                            small
                            color="grey darken-1"
                            v-bind="attrs"
                            v-on="on"
                            @click="cancelReadyTurn(item)"
                          >
                            <v-icon small>mdi-close-circle-outline</v-icon>
                          </v-btn>
                        </template>
                        <span>Descartar / Cancelar Turno</span>
                      </v-tooltip>
                    </div>
                  </v-card>
                </div>

                <div v-else class="text-center py-8 grey--text">
                  <v-icon size="40" color="grey lighten-1" class="mb-2"
                    >mdi-account-check-outline</v-icon
                  >
                  <div class="caption">
                    No hay colaboradores con documentos pendientes de atención.
                  </div>
                </div>
              </v-tab-item>

              <!-- TAB 2: EN TERMINALES -->
              <v-tab-item>
                <div v-if="activeCallsList.length > 0">
                  <v-card
                    v-for="(item, idx) in activeCallsList"
                    :key="item.TurnId || idx"
                    outlined
                    class="pa-4 mb-3 rounded-lg d-flex align-center justify-space-between flex-wrap gap-2"
                  >
                    <div>
                      <div class="d-flex align-center">
                        <v-chip
                          small
                          color="amber darken-2"
                          dark
                          class="font-weight-bold mr-2"
                        >
                          {{ item.AssignedKiosk || "Terminal" }}
                        </v-chip>
                        <strong class="subtitle-2 text--primary">{{
                          item.FullName
                        }}</strong>
                        <span class="caption grey--text ml-2 font-mono"
                          >DNI: {{ item.Dni }}</span
                        >
                      </div>
                      <div class="caption grey--text text--darken-2 mt-1">
                        Turno: <strong>{{ item.TurnCode }}</strong> | Estado:
                        <span class="blue--text font-weight-bold">{{
                          item.Status === "READING"
                            ? "Leyendo Documentos"
                            : "Llamado en Pantalla"
                        }}</span>
                      </div>
                    </div>
                    <div class="d-flex align-center gap-2">
                      <v-chip
                        small
                        outlined
                        :color="
                          item.Status === 'READING' ? 'blue' : 'amber darken-3'
                        "
                      >
                        {{
                          item.Status === "READING"
                            ? "En Lectura"
                            : "Esperando Inicio"
                        }}
                      </v-chip>
                      <v-btn
                        x-small
                        color="red darken-1"
                        outlined
                        rounded
                        class="text-none ml-2 font-weight-bold"
                        @click="cancelOrReleaseStuckTurn(item)"
                        title="Liberar este terminal y cancelar atención colgada"
                      >
                        <v-icon x-small left>mdi-close-circle-outline</v-icon>
                        Liberar
                      </v-btn>
                    </div>
                  </v-card>
                </div>
                <div v-else class="text-center py-8 grey--text">
                  <v-icon size="40" color="grey lighten-1" class="mb-2"
                    >mdi-tablet</v-icon
                  >
                  <div class="caption">
                    No hay colaboradores activos en los Terminales actualmente.
                  </div>
                </div>
              </v-tab-item>

              <!-- TAB 3: EN ESPERA -->
              <v-tab-item>
                <div v-if="waitingList.length > 0">
                  <v-card
                    v-for="(item, idx) in waitingList"
                    :key="item.TurnId || idx"
                    outlined
                    class="pa-3 mb-2 rounded-lg d-flex align-center justify-space-between"
                  >
                    <div class="d-flex align-center">
                      <v-chip
                        small
                        outlined
                        color="cyan darken-2"
                        class="font-weight-bold mr-2"
                      >
                        #{{ idx + 1 }} {{ item.TurnCode }}
                      </v-chip>
                      <strong class="body-2 text--primary">{{
                        item.FullName
                      }}</strong>
                      <span class="caption grey--text ml-2 font-mono"
                        >DNI: {{ item.Dni }}</span
                      >
                    </div>
                    <span class="caption grey--text">En cola de espera</span>
                  </v-card>
                </div>
                <div v-else class="text-center py-8 grey--text">
                  <v-icon size="40" color="grey lighten-1" class="mb-2"
                    >mdi-account-multiple-outline</v-icon
                  >
                  <div class="caption">La cola de espera está vacía.</div>
                </div>
              </v-tab-item>
            </v-tabs-items>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- MODAL DE ATENCIÓN: CAPTURA DE FIRMA Y HUELLA CON FLUJO POR PASOS -->
    <v-dialog v-model="showDetailsModal" max-width="960" persistent>
      <v-card v-if="selectedTurn" class="pa-5 rounded-2xl elevation-6 bg-white">
        <!-- Header del Modal -->
        <v-card-title
          class="d-flex align-center justify-space-between border-bottom pb-3 pt-1 px-3"
        >
          <div class="d-flex align-center">
            <v-avatar
              color="primary"
              size="46"
              class="mr-3 white--text font-weight-bold elevation-2"
            >
              <v-icon dark>mdi-account-check</v-icon>
            </v-avatar>
            <div>
              <div class="text-h6 font-weight-bold text--primary d-flex align-center flex-wrap">
                <span>{{ selectedTurn.FullName }}</span>
                <v-chip
                  v-if="selectedTurn.AssignedKiosk"
                  small
                  color="amber darken-3"
                  dark
                  class="font-weight-bold ml-2 elevation-1"
                >
                  <v-icon x-small left>mdi-monitor-cellphone</v-icon>
                  Módulo: {{ selectedTurn.AssignedKiosk }}
                </v-chip>
                <v-chip
                  v-if="selectedTurn.HasMedicalAptitude"
                  small
                  color="green darken-1"
                  dark
                  class="font-weight-bold ml-2 elevation-1"
                >
                  <v-icon x-small left>mdi-stethoscope</v-icon>
                  Aptitud OK
                </v-chip>
                <v-chip
                  v-if="contractDetails && contractDetails.AgtIsEmailSent"
                  small
                  color="teal darken-2"
                  dark
                  class="font-weight-bold ml-2 elevation-1"
                >
                  <v-icon x-small left>mdi-email-check</v-icon>
                  Correo Enviado
                </v-chip>
              </div>
              <div class="caption grey--text mt-1">
                DNI: <strong>{{ selectedTurn.Dni }}</strong> | Turno:
                <strong class="primary--text">{{ selectedTurn.TurnCode }}</strong> | Puesto:
                <strong>{{ selectedTurn.PositionName || "General" }}</strong>
              </div>
            </div>
          </div>
          <div class="d-flex align-center">
            <!-- Botón para Reenviar Correo si el contrato ya está firmado -->
            <v-btn
              v-if="contractDetails && (contractDetails.AgtStatus === 1 || contractDetails.AgtIsSigned === 1)"
              color="blue darken-2"
              dark
              rounded
              small
              class="text-none font-weight-bold mr-2 elevation-2"
              :loading="resendingEmail"
              @click="resendSignedEmail(selectedTurn)"
            >
              <v-icon left small>mdi-email-sync-outline</v-icon>
              Reenviar Correo
            </v-btn>

            <!-- Botón de Digitalización de DNI -->
            <v-btn
              :color="hasDniPhotos ? 'teal darken-1' : 'teal lighten-1'"
              dark
              rounded
              small
              class="text-none font-weight-bold mr-2 elevation-2"
              @click="openDniModal"
            >
              <v-icon left small>mdi-card-account-details-outline</v-icon>
              {{ hasDniPhotos ? "DNI Digitalizado (OK)" : "Tomar Fotos DNI" }}
              <v-icon right x-small v-if="hasDniPhotos">mdi-check-bold</v-icon>
            </v-btn>

            <v-btn
              icon
              small
              @click="showDetailsModal = false"
              :disabled="isFinalizing"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text class="pa-4">
          <!-- Pestañas / Stepper de Flujo de Atención -->
          <div class="mb-4">
            <v-tabs
              v-model="modalStep"
              grow
              dense
              color="primary"
              class="rounded-xl border elevation-0"
              slider-color="primary"
            >
              <v-tab class="text-none font-weight-bold py-3">
                <v-icon left small :color="modalStep === 0 ? 'primary' : 'grey'">mdi-draw</v-icon>
                <span>1. Captura Biométrica (Firma y Huella)</span>
                <v-icon
                  v-if="signatureBase64"
                  x-small
                  color="success"
                  class="ml-2"
                >mdi-check-circle</v-icon>
              </v-tab>
              <v-tab class="text-none font-weight-bold py-3">
                <v-icon left small :color="modalStep === 1 ? 'primary' : 'grey'">mdi-qrcode-scan</v-icon>
                <span>2. Entrega Acceso ARI & Finalizar</span>
              </v-tab>
            </v-tabs>
          </div>

          <!-- Banner de Fecha y Hora en Tiempo Real y Estado DNI -->
          <div class="d-flex align-center justify-space-between flex-wrap mb-4 gap-2">
            <v-alert
              dense
              color="blue lighten-5"
              class="border-blue ma-0 flex-grow-1 d-flex align-center"
            >
              <v-icon color="primary" class="mr-2"
                >mdi-clock-check-outline</v-icon
              >
              <span class="caption text--primary">
                Fecha y Hora de Firma del Contrato:
                <strong>{{ currentTimestampString }}</strong>
              </span>
            </v-alert>

            <v-chip
              v-if="hasDniPhotos"
              color="teal lighten-5"
              class="teal--text text--darken-3 font-weight-bold"
              outlined
            >
              <v-icon x-small left color="teal darken-2">mdi-check-circle</v-icon>
              DNI Digitalizado (Ambas Caras)
            </v-chip>
          </div>

          <!-- PASO 1: CAPTURA BIOMÉTRICA (FIRMA Y HUELLA) -->
          <div v-show="modalStep === 0">
            <v-row>
              <!-- Panel 1: Firma Digital (SSignaturePad - Wacom / Manual) -->
              <v-col cols="12" md="7">
                <div
                  class="subtitle-2 font-weight-bold primary--text mb-2 d-flex align-center justify-space-between flex-wrap"
                  style="gap: 6px"
                >
                  <div class="d-flex align-center">
                    <v-icon small left color="primary">mdi-draw</v-icon>
                    <span>Firma Digital</span>
                  </div>
                  <div>
                    <v-btn-toggle
                      v-model="signatureInputMode"
                      mandatory
                      dense
                      color="primary"
                      class="elevation-1"
                    >
                      <v-btn value="wacom" small class="text-none">
                        <v-icon left x-small color="primary">mdi-tablet</v-icon>
                        Wacom
                      </v-btn>
                      <v-btn value="manual" small class="text-none">
                        <v-icon left x-small color="grey darken-2">mdi-draw-pen</v-icon>
                        Mouse
                      </v-btn>
                    </v-btn-toggle>
                  </div>
                </div>
                <v-card outlined class="pa-3 rounded-lg bg-grey-lighten-5">
                  <s-signature-pad
                    ref="signaturePadRef"
                    :wacom="useWacom"
                    :wacom-full-name="selectedTurn ? selectedTurn.FullName : ''"
                    @save-signature="onSignatureSaved"
                    @save="onSignatureSaved"
                    @change="onSignatureChanged"
                  />
                  <div
                    v-if="signatureBase64"
                    class="caption green--text text--darken-2 font-weight-bold mt-2 d-flex align-center justify-space-between"
                  >
                    <div class="d-flex align-center">
                      <v-icon x-small color="green darken-2" class="mr-1"
                        >mdi-check-circle</v-icon
                      >
                      <span>Firma capturada correctamente.</span>
                    </div>
                    <v-btn
                      x-small
                      text
                      color="grey darken-2"
                      @click="clearSignature"
                    >
                      <v-icon x-small left>mdi-refresh</v-icon>
                      Limpiar / Re-firmar
                    </v-btn>
                  </div>
                </v-card>
              </v-col>

              <!-- Panel 2: Huella Digital (Exclusivo Biométrico) -->
              <v-col cols="12" md="5">
                <div
                  class="subtitle-2 font-weight-bold primary--text mb-2 d-flex align-center"
                >
                  <v-icon small left color="primary">mdi-fingerprint</v-icon>
                  <span>Huella Digital Biométrica</span>
                </div>
                <v-card
                  outlined
                  class="pa-3 rounded-lg text-center bg-grey-lighten-5 d-flex flex-column align-center justify-center fill-height"
                >
                  <!-- Botón Biométrico con componente sFingerPrint -->
                  <div class="mb-3 w-100">
                    <s-finger-print
                      text="Capturar Huella"
                      color="primary"
                      :disableCapture="false"
                      :btnProps="{
                        rounded: true,
                        dark: true,
                        block: true,
                        medium: true,
                        class: 'font-weight-bold elevation-2',
                      }"
                      @fingerprintSaved="onFingerprintSaved"
                    />
                  </div>

                  <!-- Vista previa de Huella Biometria -->
                  <div
                    v-if="fingerprintBase64"
                    class="fingerprint-preview-box border pa-2 rounded-lg elevation-2 bg-white mb-2"
                    style="
                      width: 120px;
                      height: 140px;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                    "
                  >
                    <img
                      :src="
                        fingerprintBase64.startsWith('data:')
                          ? fingerprintBase64
                          : 'data:image/png;base64,' + fingerprintBase64
                      "
                      style="
                        max-width: 100%;
                        max-height: 100%;
                        object-fit: contain;
                      "
                      alt="Huella biométrica capturada"
                    />
                  </div>
                  <div
                    v-else
                    class="huella-empty-box border-dashed pa-4 rounded-lg d-flex flex-column align-center justify-center mb-2"
                    style="width: 120px; height: 140px; background: #fafafa"
                  >
                    <v-icon size="40" color="grey lighten-1"
                      >mdi-fingerprint</v-icon
                    >
                    <span
                      class="caption grey--text mt-1 text-center"
                      style="font-size: 11px"
                      >Pendiente de Captura</span
                    >
                  </div>

                  <div
                    v-if="fingerprintBase64"
                    class="d-flex align-center flex-column"
                  >
                    <div
                      class="caption green--text text--darken-2 font-weight-bold d-flex align-center mb-1"
                    >
                      <v-icon x-small color="green darken-2" class="mr-1"
                        >mdi-check-decagram</v-icon
                      >
                      Huella OK
                    </div>
                    <v-btn
                      x-small
                      text
                      color="grey darken-2"
                      @click="fingerprintBase64 = null"
                    >
                      <v-icon x-small left>mdi-refresh</v-icon>
                      Volver a Capturar
                    </v-btn>
                  </div>
                </v-card>
              </v-col>
            </v-row>

            <!-- Documentos que serán firmados y archivados -->
            <div class="mt-4">
              <div class="caption font-weight-bold text--primary mb-2">
                <v-icon x-small color="primary" class="mr-1"
                  >mdi-file-document-multiple-outline</v-icon
                >
                Documentos que se generarán con firma, huella y timestamp:
              </div>
              <v-chip-group column>
                <v-chip
                  x-small
                  color="blue lighten-4"
                  class="blue--text text--darken-3 font-weight-bold"
                >
                  📄 Contrato de Trabajo (Modalidad)
                </v-chip>
                <v-chip
                  x-small
                  color="teal lighten-4"
                  class="teal--text text--darken-3 font-weight-bold"
                >
                  📝 Consentimiento ARI Sunshine
                </v-chip>
                <v-chip
                  x-small
                  color="teal lighten-4"
                  class="teal--text text--darken-3 font-weight-bold"
                >
                  📝 DJ de Domicilio
                </v-chip>
                <v-chip
                  x-small
                  color="teal lighten-4"
                  class="teal--text text--darken-3 font-weight-bold"
                >
                  📝 DJ de Antecedentes
                </v-chip>
                <v-chip
                  x-small
                  color="teal lighten-4"
                  class="teal--text text--darken-3 font-weight-bold"
                >
                  📝 Código de Conducta
                </v-chip>
                <v-chip
                  x-small
                  color="teal lighten-4"
                  class="teal--text text--darken-3 font-weight-bold"
                >
                  📝 Descuento de Comedor
                </v-chip>
                <v-chip
                  x-small
                  color="teal lighten-4"
                  class="teal--text text--darken-3 font-weight-bold"
                >
                  📝 RISST
                </v-chip>
                <v-chip
                  x-small
                  color="teal lighten-4"
                  class="teal--text text--darken-3 font-weight-bold"
                >
                  📝 RIT
                </v-chip>
                <v-chip
                  x-small
                  color="indigo lighten-4"
                  class="indigo--text text--darken-3 font-weight-bold"
                >
                  🏥 Certificado de Aptitud Médica
                </v-chip>
              </v-chip-group>
            </div>
          </div>

          <!-- PASO 2: CONFIRMACIÓN Y ENTREGA DE ACCESO ARI SUNSHINE -->
          <div v-show="modalStep === 1" class="py-2">
            <v-card
              outlined
              class="pa-6 rounded-2xl text-center bg-blue-grey-lighten-5 border-ari"
            >
              <v-row class="align-center justify-center">
                <v-col cols="12" md="5" class="text-center">
                  <div class="bg-white pa-4 rounded-xl border elevation-2 d-inline-block">
                    <qrcode-vue
                      v-if="ariQrValue"
                      :value="ariQrValue"
                      :size="180"
                      level="Q"
                      render-as="svg"
                    />
                    <v-icon v-else size="120" color="grey lighten-2">mdi-qrcode</v-icon>
                    <div class="caption font-weight-black primary--text mt-2" style="font-size: 13px;">
                      ARI SUNSHINE
                    </div>
                    <div class="caption grey--text text--darken-2 font-mono" style="font-size: 11px;">
                      DNI: <strong>{{ (selectedTurn && selectedTurn.Dni) || '-' }}</strong>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="7" class="text-left pl-md-4">
                  <div class="d-flex align-center mb-2">
                    <v-avatar size="36" color="primary" class="mr-2 white--text font-weight-bold">
                      <v-icon small dark>mdi-cellphone-check</v-icon>
                    </v-avatar>
                    <div>
                      <h3 class="subtitle-1 font-weight-black primary--text mb-0">
                        Acceso Digital a Boletas de Pago
                      </h3>
                      <span class="caption grey--text text--darken-2">
                        Portal de Autoservicio ARI Sunshine
                      </span>
                    </div>
                  </div>

                  <v-alert
                    dense
                    text
                    type="success"
                    class="rounded-lg text-caption font-weight-medium my-3"
                  >
                    Firma y huella listas para estamparse en el lote de documentos contractuales.
                  </v-alert>

                  <p class="body-2 grey--text text--darken-3 mb-3">
                    Indique al colaborador que <strong>escanee este código QR</strong> con la cámara de su celular para registrarse y visualizar sus boletas de pago digitales, o que ingrese a <strong>https://ari.sunshineperu.com</strong>.
                  </p>

                  <div class="d-flex align-center flex-wrap gap-2">
                    <v-chip small color="primary" outlined class="font-weight-bold">
                      <v-icon x-small left>mdi-account-circle</v-icon>
                      Usuario: {{ (selectedTurn && selectedTurn.Dni) || '-' }}
                    </v-chip>
                    <v-chip small color="teal" outlined class="font-weight-bold" v-if="hasDniPhotos">
                      <v-icon x-small left>mdi-check-decagram</v-icon>
                      DNI Digitalizado
                    </v-chip>
                  </div>
                </v-col>
              </v-row>
            </v-card>
          </div>
        </v-card-text>

        <!-- Botones de Acción del Modal -->
        <v-card-actions class="px-4 pb-2 border-top pt-3">
          <v-btn
            v-if="modalStep === 0"
            text
            color="grey darken-1"
            @click="showDetailsModal = false"
            :disabled="isFinalizing"
          >
            Cancelar
          </v-btn>
          <v-btn
            v-else
            outlined
            color="grey darken-2"
            rounded
            class="text-none font-weight-bold px-4"
            @click="modalStep = 0"
            :disabled="isFinalizing"
          >
            <v-icon left>mdi-arrow-left</v-icon>
            Volver a Biometría
          </v-btn>

          <v-spacer />

          <v-btn
            v-if="selectedTurn && selectedTurn.AssignedKiosk"
            color="orange darken-3"
            outlined
            rounded
            class="font-weight-bold px-4 mr-2 text-none"
            :disabled="isFinalizing"
            @click="manualReleaseTurnKiosk(selectedTurn)"
          >
            <v-icon left>mdi-monitor-lock-open</v-icon>
            Liberar {{ selectedTurn.AssignedKiosk }}
          </v-btn>

          <!-- Botón Siguiente si está en Paso 1 -->
          <v-btn
            v-if="modalStep === 0"
            color="primary"
            rounded
            large
            class="font-weight-bold px-6 elevation-2 text-none"
            :disabled="!signatureBase64"
            @click="modalStep = 1"
          >
            Continuar a Entrega ARI
            <v-icon right>mdi-arrow-right</v-icon>
          </v-btn>

          <!-- Botón Finalizar si está en Paso 2 -->
          <v-btn
            v-else
            color="success darken-1"
            dark
            rounded
            large
            class="font-weight-bold px-6 elevation-3 text-none"
            :loading="isFinalizing"
            :disabled="isFinalizing || (!signatureBase64 && !fingerprintBase64)"
            @click="confirmAndFinalizeContract"
          >
            <v-icon left>mdi-check-decagram</v-icon>
            CONFIRMAR FIRMA Y FINALIZAR CONTRATO
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog de Progreso de Generación de Archivos -->
    <v-dialog v-model="isFinalizing" max-width="400" persistent>
      <v-card class="pa-6 text-center rounded-xl">
        <v-progress-circular
          indeterminate
          color="primary"
          size="50"
          width="5"
          class="mb-4"
        />
        <div class="subtitle-1 font-weight-bold primary--text">
          Generando y Archivando Contratos Firmados
        </div>
        <div class="caption grey--text mt-2">
          Estampando firma, huella y fecha/hora exacta en todos los formatos...
        </div>
      </v-card>
    </v-dialog>

    <!-- Modal de Consulta y Validación de Contratos Pendientes -->
    <v-dialog v-model="validationDialog" max-width="1200px" scrollable>
      <v-card class="rounded-xl">
        <v-card-title class="primary white--text py-3 px-5 d-flex align-center">
          <v-icon color="white" class="mr-2">mdi-clipboard-check-multiple-outline</v-icon>
          <span class="text-h6 font-weight-bold">Control de Validación: Contratos, Aptitud y Dirección</span>
          <v-spacer />
          <v-btn
            color="amber darken-2"
            dark
            small
            rounded
            class="text-none font-weight-bold mr-2 elevation-1"
            :loading="loadingValidation"
            @click="loadValidationStatus(true)"
          >
            <v-icon left small>mdi-refresh</v-icon>
            Ejecutar / Revalidar Job
          </v-btn>
          <v-btn icon dark @click="validationDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4">
          <!-- Resumen de Métricas -->
          <v-row dense class="mb-3">
            <v-col cols="12" sm="6" md="2">
              <v-card outlined class="pa-3 text-center rounded-lg bg-light elevation-0">
                <div class="caption grey--text font-weight-bold">TOTAL EVALUADOS</div>
                <div class="text-h5 font-weight-bold primary--text">{{ validationSummary.total }}</div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-card outlined class="pa-3 text-center rounded-lg elevation-0" color="green lighten-5">
                <div class="caption green--text text--darken-3 font-weight-bold">ENCOLADOS / LISTOS</div>
                <div class="text-h5 font-weight-bold green--text text--darken-3">{{ validationSummary.enqueued }}</div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-card outlined class="pa-3 text-center rounded-lg elevation-0" color="red lighten-5">
                <div class="caption red--text text--darken-3 font-weight-bold">FALTA DIRECCIÓN DNI</div>
                <div class="text-h5 font-weight-bold red--text text--darken-3">{{ validationSummary.missingAddress }}</div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-card outlined class="pa-3 text-center rounded-lg elevation-0" color="orange lighten-5">
                <div class="caption orange--text text--darken-3 font-weight-bold">FALTA APTITUD MÉDICA</div>
                <div class="text-h5 font-weight-bold orange--text text--darken-3">{{ validationSummary.missingAptitude }}</div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-card outlined class="pa-3 text-center rounded-lg elevation-0" color="blue lighten-5">
                <div class="caption blue--text text--darken-3 font-weight-bold">YA EN COLA</div>
                <div class="text-h5 font-weight-bold blue--text text--darken-3">{{ validationSummary.alreadyEnqueued }}</div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Filtros y Búsqueda -->
          <v-row dense class="mb-2 align-center">
            <v-col cols="12" md="6">
              <v-text-field
                v-model="validationSearch"
                dense
                outlined
                clearable
                hide-details
                prepend-inner-icon="mdi-magnify"
                placeholder="Buscar por DNI, Nombre o Puesto..."
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-btn-toggle v-model="validationFilterTab" mandatory dense class="d-flex">
                <v-btn small class="text-none flex-grow-1" value="ALL">Todos ({{ validationList.length }})</v-btn>
                <v-btn small class="text-none flex-grow-1 red--text font-weight-bold" value="MISSING_ADDRESS">Falta Dirección ({{ validationSummary.missingAddress }})</v-btn>
                <v-btn small class="text-none flex-grow-1 orange--text font-weight-bold" value="MISSING_APTITUDE">Falta Aptitud ({{ validationSummary.missingAptitude }})</v-btn>
                <v-btn small class="text-none flex-grow-1 green--text font-weight-bold" value="READY">Listos ({{ validationSummary.enqueued }})</v-btn>
              </v-btn-toggle>
            </v-col>
          </v-row>

          <!-- Tabla de Resultados -->
          <v-data-table
            :headers="validationHeaders"
            :items="filteredValidationList"
            :loading="loadingValidation"
            :search="validationSearch"
            dense
            class="elevation-1 rounded-lg"
            :items-per-page="10"
            no-data-text="No hay trabajadores pendientes por evaluar."
          >
            <template v-slot:[`item.Dni`]="{ item }">
              <span class="font-weight-bold">{{ item.Dni }}</span>
            </template>

            <template v-slot:[`item.HasMedicalAptitude`]="{ item }">
              <div class="d-flex align-center justify-center flex-wrap" style="gap: 4px">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip
                      x-small
                      :color="item.HasMedicalAptitude ? 'green' : 'orange darken-2'"
                      dark
                      class="font-weight-bold"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon x-small left>{{ item.HasMedicalAptitude ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
                      {{ item.HasMedicalAptitude ? (item.MevStatusName || 'Apto Médico') : 'Falta Aptitud' }}
                      <span v-if="item.HasMedicalAptitude && item.DaysSinceEvaluation !== null && item.DaysSinceEvaluation !== undefined" class="ml-1 font-weight-regular">
                        · {{ item.DaysSinceEvaluation }}d
                      </span>
                    </v-chip>
                  </template>
                  <span>
                    {{ item.HasMedicalAptitude ? `Evaluación #${item.MevID || ''} ${item.DocName ? 'por ' + item.DocName : ''} (hace ${item.DaysSinceEvaluation || 0} días).` : 'Sin evaluación médica apta registrada en el último año.' }}
                    <span v-if="item.AssignedMevID"> (Asignada manualmente).</span>
                  </span>
                </v-tooltip>
                <v-btn
                  icon
                  x-small
                  color="primary"
                  title="Vincular / Cambiar Aptitud Médica"
                  @click="openAssignMedicalDialog(item)"
                >
                  <v-icon small>mdi-stethoscope</v-icon>
                </v-btn>
              </div>
            </template>

            <template v-slot:[`item.HasValidDniAddress`]="{ item }">
              <v-chip
                x-small
                :color="item.HasValidDniAddress ? 'green' : 'red darken-2'"
                dark
                class="font-weight-bold"
              >
                <v-icon x-small left>{{ item.HasValidDniAddress ? 'mdi-map-marker-check' : 'mdi-map-marker-remove' }}</v-icon>
                {{ item.HasValidDniAddress ? 'Dirección DNI OK' : 'Falta Dirección DNI' }}
              </v-chip>
            </template>

            <template v-slot:[`item.AgtIsEmailSent`]="{ item }">
              <v-tooltip bottom v-if="item.AgtIsEmailSent">
                <template v-slot:activator="{ on, attrs }">
                  <v-chip
                    x-small
                    color="green"
                    dark
                    class="font-weight-bold"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon x-small left>mdi-email-check</v-icon>
                    Enviado
                  </v-chip>
                </template>
                <span>
                  Enviado a: {{ item.AgtEmailRecipient || 'Correo del colaborador' }}
                  <span v-if="item.AgtEmailSentDate"> el {{ formatDateTime(item.AgtEmailSentDate) }}</span>
                </span>
              </v-tooltip>
              <v-chip
                v-else
                x-small
                color="grey lighten-2"
                class="font-weight-bold grey--text text--darken-3"
              >
                <v-icon x-small left>mdi-email-outline</v-icon>
                Pendiente
              </v-chip>
            </template>

            <template v-slot:[`item.ActionTaken`]="{ item }">
              <v-chip
                small
                outlined
                :color="getActionColor(item.ActionTaken)"
                class="font-weight-bold"
              >
                {{ getActionLabel(item.ActionTaken) }}
              </v-chip>
            </template>

            <template v-slot:[`item.ValidationMessage`]="{ item }">
              <span :class="getMessageClass(item.ActionTaken)">{{ item.ValidationMessage }}</span>
            </template>

            <template v-slot:[`item.actions`]="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    @click="openAssignMedicalDialog(item)"
                  >
                    <v-icon small>mdi-stethoscope</v-icon>
                  </v-btn>
                </template>
                <span>Asignar / Cambiar Aptitud Médica</span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions class="pa-3 border-top justify-end">
          <v-btn text color="grey darken-1" @click="validationDialog = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal para Asignar / Vincular Aptitud Médica al Contrato -->
    <v-dialog v-model="assignMedicalDialog" max-width="900px" scrollable>
      <v-card class="rounded-xl border-light bg-white" outlined elevation="0">
        <v-card-title class="primary white--text py-3 px-5 d-flex align-center">
          <v-icon color="white" class="mr-2">mdi-stethoscope</v-icon>
          <span class="text-subtitle-1 font-weight-bold">
            Vincular Aptitud Médica al Contrato
          </span>
          <v-spacer />
          <v-btn icon dark @click="assignMedicalDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4">
          <!-- Datos del Colaborador -->
          <v-card outlined class="pa-3 mb-3 rounded-lg border-light bg-light" elevation="0" v-if="selectedWorkerForMedical">
            <v-row dense align="center">
              <v-col cols="12" sm="5">
                <div class="caption grey--text text--darken-2">Colaborador:</div>
                <div class="subtitle-2 font-weight-bold grey--text text--darken-4">
                  {{ selectedWorkerForMedical.FullName }}
                </div>
              </v-col>
              <v-col cols="6" sm="3">
                <div class="caption grey--text text--darken-2">DNI:</div>
                <div class="subtitle-2 font-weight-bold grey--text text--darken-4">
                  {{ selectedWorkerForMedical.Dni }}
                </div>
              </v-col>
              <v-col cols="6" sm="4">
                <div class="caption grey--text text--darken-2">Puesto:</div>
                <div class="subtitle-2 font-weight-bold grey--text text--darken-4">
                  {{ selectedWorkerForMedical.PositionName || "General" }} (Contrato #{{ selectedWorkerForMedical.AgtID }})
                </div>
              </v-col>
            </v-row>
          </v-card>

          <!-- Nota informativa de Ley / Validez -->
          <v-alert dense outlined type="info" class="mb-3 text-caption rounded-lg">
            Las evaluaciones médicas tienen validez de hasta <strong>1 año (365 días)</strong> para reingresantes o contratos con inicio consecutivo. Seleccione la evaluación correspondiente para vincularla formalmente a este contrato.
          </v-alert>

          <!-- Tabla de Evaluaciones Médicas Históricas del Trabajador -->
          <v-data-table
            :headers="workerMedicalHeaders"
            :items="workerMedicalList"
            :loading="loadingWorkerMedicalList"
            dense
            class="elevation-1 rounded-lg"
            :items-per-page="5"
            no-data-text="No se encontraron evaluaciones médicas para este colaborador."
          >
            <template v-slot:[`item.MevDate`]="{ item }">
              <span class="font-weight-medium">{{ formatDisplayDate(item.MevDate) }}</span>
            </template>

            <template v-slot:[`item.MevStatusName`]="{ item }">
              <v-chip
                x-small
                :color="item.MevStatus === 1 ? 'green' : item.MevStatus === 2 ? 'blue' : item.MevStatus === 4 ? 'orange darken-2' : 'red'"
                dark
                class="font-weight-bold"
              >
                {{ item.MevStatusName || (item.MevStatus === 1 ? 'Apto' : item.MevStatus === 2 ? 'Apto c/ Restricciones' : 'No Apto') }}
              </v-chip>
            </template>

            <template v-slot:[`item.Vigencia`]="{ item }">
              <v-chip
                x-small
                :color="item.IsValidOneYear ? 'green' : 'grey darken-1'"
                outlined
                class="font-weight-bold"
              >
                <v-icon x-small left>{{ item.IsValidOneYear ? 'mdi-clock-check-outline' : 'mdi-clock-alert-outline' }}</v-icon>
                {{ item.IsValidOneYear ? `Vigente (${item.DaysSinceEvaluation || 0}d)` : `Vencido (${item.DaysSinceEvaluation || 0}d)` }}
              </v-chip>
            </template>

            <template v-slot:[`item.actions`]="{ item }">
              <v-chip
                v-if="selectedWorkerForMedical && (selectedWorkerForMedical.AssignedMevID === item.MevID || selectedWorkerForMedical.MevID === item.MevID)"
                x-small
                color="primary"
                dark
                class="font-weight-bold"
              >
                <v-icon x-small left>mdi-check</v-icon>
                Asignado
              </v-chip>
              <v-btn
                v-else
                x-small
                color="primary"
                class="text-none font-weight-bold rounded-lg px-2"
                :loading="assigningMedicalId === item.MevID"
                @click="confirmAssignMedical(item)"
              >
                <v-icon x-small left>mdi-link-variant</v-icon>
                Vincular
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions class="pa-3 border-top justify-end">
          <v-btn text color="grey darken-1" @click="assignMedicalDialog = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- CONTENEDOR OCULTO PARA COMPILACIÓN DE PDFS -->
    <div
      v-if="selectedTurn && contractDetails"
      class="hidden-pdf-render-container"
      style="position: absolute; left: -9999px; top: -9999px; width: 800px"
    >
      <!-- 1. Contrato según modalidad -->
      <div ref="pdfContractDoc" class="pdf-target-doc">
        <pdf-contract-por-temporada
          v-if="selectedTurn.TypeContract === 11 || !selectedTurn.TypeContract"
          :data="contractDetails"
          :signature-data="signatureBase64"
          :fingerprint-data="fingerprintBase64"
          :signed-timestamp="signedTimestampValue"
          :show-salary="true"
          prevent-individual-download
        />
        <pdf-contract-por-incremento-actividades
          v-else-if="selectedTurn.TypeContract === 12"
          :data="contractDetails"
          :signature-data="signatureBase64"
          :fingerprint-data="fingerprintBase64"
          :signed-timestamp="signedTimestampValue"
          :show-salary="true"
          prevent-individual-download
        />
        <pdf-contract-por-servicio-especifico
          v-else
          :data="contractDetails"
          :signature-data="signatureBase64"
          :fingerprint-data="fingerprintBase64"
          :signed-timestamp="signedTimestampValue"
          :show-salary="true"
          prevent-individual-download
        />
      </div>

      <!-- 2. Formatos -->
      <div ref="pdfAddressDoc" class="pdf-target-doc">
        <format-address-declaration
          :worker-data="contractDetails"
          :contract-data="contractDetails"
          :signature-data="signatureBase64"
          :fingerprint-data="fingerprintBase64"
          :signed-timestamp="signedTimestampValue"
        />
      </div>

      <div ref="pdfBackgroundDoc" class="pdf-target-doc">
        <format-background-declaration
          :worker-data="contractDetails"
          :contract-data="contractDetails"
          :signature-data="signatureBase64"
          :fingerprint-data="fingerprintBase64"
          :signed-timestamp="signedTimestampValue"
        />
      </div>

      <div ref="pdfCodeOfConductDoc" class="pdf-target-doc">
        <format-code-of-conduct
          :worker-data="contractDetails"
          :contract-data="contractDetails"
          :signature-data="signatureBase64"
          :fingerprint-data="fingerprintBase64"
          :signed-timestamp="signedTimestampValue"
        />
      </div>

      <div ref="pdfDiningDoc" class="pdf-target-doc">
        <format-dining-deduction
          :worker-data="contractDetails"
          :contract-data="contractDetails"
          :signature-data="signatureBase64"
          :fingerprint-data="fingerprintBase64"
          :signed-timestamp="signedTimestampValue"
        />
      </div>

      <div ref="pdfRisstDoc" class="pdf-target-doc">
        <format-risst
          :worker-data="contractDetails"
          :contract-data="contractDetails"
          :signature-data="signatureBase64"
          :fingerprint-data="fingerprintBase64"
          :signed-timestamp="signedTimestampValue"
        />
      </div>

      <div ref="pdfRitDoc" class="pdf-target-doc">
        <format-rit
          :worker-data="contractDetails"
          :contract-data="contractDetails"
          :signature-data="signatureBase64"
          :fingerprint-data="fingerprintBase64"
          :signed-timestamp="signedTimestampValue"
        />
      </div>

      <div ref="pdfSubstanceDoc" class="pdf-target-doc">
        <format-substance-declaration
          :worker-data="contractDetails"
          :contract-data="contractDetails"
          :signature-data="signatureBase64"
          :fingerprint-data="fingerprintBase64"
          :signed-timestamp="signedTimestampValue"
        />
      </div>

      <div ref="pdfAriDoc" class="pdf-target-doc">
        <format-digital-consent-ari
          :worker-data="contractDetails"
          :contract-data="contractDetails"
          :signature-data="signatureBase64"
          :fingerprint-data="fingerprintBase64"
          :signed-timestamp="signedTimestampValue"
        />
      </div>

      <div ref="pdfAriDeliverableDoc" class="pdf-target-doc">
        <format-digital-consent-ari-deliverable
          :worker-data="contractDetails"
          :contract-data="contractDetails"
          :signed-timestamp="signedTimestampValue"
        />
      </div>

      <div ref="pdfSstCriticalDoc" class="pdf-target-doc">
        <format-s-s-t-critical
          :worker-data="contractDetails"
          :contract-data="contractDetails"
          :signature-data="signatureBase64"
          :fingerprint-data="fingerprintBase64"
          :signed-timestamp="signedTimestampValue"
        />
      </div>

      <div ref="pdfOvertimeDoc" class="pdf-target-doc">
        <format-overtime-compensation
          :worker-data="contractDetails"
          :contract-data="contractDetails"
          :signature-data="signatureBase64"
          :fingerprint-data="fingerprintBase64"
          :signed-timestamp="signedTimestampValue"
        />
      </div>
    </div>

    <!-- Modal de Captura y Digitalización de DNI (Anverso y Reverso) -->
    <dni-photo-capture-modal
      v-model="showDniModal"
      :dni="(contractDetails && (contractDetails.PrsDocumentNumber || contractDetails.Dni || contractDetails.dni)) || (selectedTurn && (selectedTurn.Dni || selectedTurn.dni)) || ''"
      :worker-name="(contractDetails && (contractDetails.NtpFullName || contractDetails.FullName || contractDetails.fullName)) || (selectedTurn && (selectedTurn.FullName || selectedTurn.fullName)) || ''"
      :position-name="(contractDetails && (contractDetails.PstName || contractDetails.PositionName || contractDetails.positionName)) || (selectedTurn && (selectedTurn.PositionName || selectedTurn.positionName)) || ''"
      :agt-id="(contractDetails && (contractDetails.AgtID || contractDetails.agtID)) || (selectedTurn && (selectedTurn.AgtID || selectedTurn.agtID)) || 0"
      :operator-name="($fun && $fun.getUserName && $fun.getUserName()) || 'OPERADOR RRHH'"
      :initial-front-image="dniFrontBase64"
      :initial-back-image="dniBackBase64"
      @saved="onDniSaved"
    />
  </v-card>
</template>

<script>
import DocumentSigningSignalrService from "@/services/HumanResource/DocumentSigningSignalrService.js";
import ContractSignService from "@/services/HumanResource/ContractSignService.js";
import _sPersonService from "@/services/General/PersonService.js";
import _sPositionService from "@/services/General/PositionService.js";
import SSignaturePad from "@/components/Utils/SSignaturePad.vue";
import SFingerPrint from "@/components/Utils/RecursosHm/sFingerPrint.vue";

import PdfContractPorTemporada from "@/views/HumanResource/PdfContract/PdfContractPorTemporada.vue";
import PdfContractPorIncrementoActividades from "@/views/HumanResource/PdfContract/PdfContractPorIncrementoActividades.vue";
import PdfContractPorServicioEspecifico from "@/views/HumanResource/PdfContract/PdfContractPorServicioEspecifico.vue";

import FormatAddressDeclaration from "@/views/HumanResource/PdfContract/Formats/FormatAddressDeclaration.vue";
import FormatBackgroundDeclaration from "@/views/HumanResource/PdfContract/Formats/FormatBackgroundDeclaration.vue";
import FormatCodeOfConduct from "@/views/HumanResource/PdfContract/Formats/FormatCodeOfConduct.vue";
import FormatDiningDeduction from "@/views/HumanResource/PdfContract/Formats/FormatDiningDeduction.vue";
import FormatRisst from "@/views/HumanResource/PdfContract/Formats/FormatRisst.vue";
import FormatRit from "@/views/HumanResource/PdfContract/Formats/FormatRit.vue";
import FormatSubstanceDeclaration from "@/views/HumanResource/PdfContract/Formats/FormatSubstanceDeclaration.vue";
import FormatOvertimeCompensation from "@/views/HumanResource/PdfContract/Formats/FormatOvertimeCompensation.vue";
import FormatDigitalConsentAri from "@/views/HumanResource/PdfContract/Formats/FormatDigitalConsentAri.vue";
import FormatDigitalConsentAriDeliverable from "@/views/HumanResource/PdfContract/Formats/FormatDigitalConsentAriDeliverable.vue";
import FormatSSTCritical from "@/views/HumanResource/PdfContract/Formats/FormatSSTCritical.vue";
import DniPhotoCaptureModal from "./DniPhotoCaptureModal.vue";
import QrcodeVue from "qrcode.vue";

import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import swal from "sweetalert2";

export default {
  name: "ContractSignView",
  components: {
    SSignaturePad,
    SFingerPrint,
    DniPhotoCaptureModal,
    QrcodeVue,
    PdfContractPorTemporada,
    PdfContractPorIncrementoActividades,
    PdfContractPorServicioEspecifico,
    FormatAddressDeclaration,
    FormatBackgroundDeclaration,
    FormatCodeOfConduct,
    FormatDiningDeduction,
    FormatRisst,
    FormatRit,
    FormatSubstanceDeclaration,
    FormatOvertimeCompensation,
    FormatDigitalConsentAri,
    FormatDigitalConsentAriDeliverable,
    FormatSSTCritical,
  },
  data() {
    return {
      inputDni: "",
      enqueueLoading: false,
      enqueueError: "",
      enqueueSuccess: "",

      activeQueueTab: 0,
      activeKiosks: [],
      readyList: [],
      activeCallsList: [],
      waitingList: [],

      selectedTurn: null,
      contractDetails: null,
      showDetailsModal: false,
      modalStep: 0,
      isFinalizing: false,

      // Digitalización de DNI (Fotos Anverso y Reverso)
      showDniModal: false,
      dniFrontBase64: null,
      dniBackBase64: null,
      hasDniPhotos: false,

      signatureInputMode: "wacom",
      signatureBase64: null,
      fingerprintBase64: null,
      signedTimestampValue: new Date(),
      currentTimestampString: "",
      timestampClockTimer: null,

      pollTimer: null,
      isFingerprintDrawing: false,

      // Control de Validación de Contratos (Aptitud y Dirección)
      validationDialog: false,
      loadingValidation: false,
      validationSearch: "",
      validationFilterTab: "ALL",
      validationList: [],
      validationSummary: {
        total: 0,
        enqueued: 0,
        missingAddress: 0,
        missingAptitude: 0,
        alreadyEnqueued: 0,
      },
      validationHeaders: [
        { text: "DNI", value: "Dni", width: "110px", sortable: true },
        { text: "Colaborador", value: "FullName", sortable: true },
        { text: "Puesto", value: "PositionName", sortable: true },
        { text: "Aptitud Médica", value: "HasMedicalAptitude", align: "center", sortable: true },
        { text: "Dirección DNI", value: "HasValidDniAddress", align: "center", sortable: true },
        { text: "Envío Correo", value: "AgtIsEmailSent", align: "center", sortable: true },
        { text: "Estado", value: "ActionTaken", align: "center", sortable: true },
        { text: "Diagnóstico / Guía", value: "ValidationMessage", sortable: false },
        { text: "Acciones", value: "actions", align: "center", width: "80px", sortable: false },
      ],

      // Asignación de Aptitud Médica al Contrato
      assignMedicalDialog: false,
      selectedWorkerForMedical: null,
      workerMedicalList: [],
      loadingWorkerMedicalList: false,
      assigningMedicalId: null,
      resendingEmail: false,
      workerMedicalHeaders: [
        { text: "ID", value: "MevID", width: "70px" },
        { text: "Fecha Eval.", value: "MevDate", width: "110px" },
        { text: "Resultado", value: "MevStatusName", align: "center", width: "140px" },
        { text: "Puesto / Área", value: "PositionName" },
        { text: "Médico Evaluador", value: "DocName" },
        { text: "Vigencia (Ley 1 Año)", value: "Vigencia", align: "center", width: "150px" },
        { text: "Acción", value: "actions", align: "center", width: "110px", sortable: false },
      ],
    };
  },
  computed: {
    useWacom() {
      return this.signatureInputMode === "wacom";
    },
    ariQrValue() {
      const dni = (
        (this.selectedTurn && this.selectedTurn.Dni) ||
        (this.contractDetails && this.contractDetails.PrsDocumentNumber) ||
        ""
      ).trim();
      if (!dni) return "";
      try {
        if (this.$fun && this.$fun.encrypt) {
          return this.$fun.encrypt(dni);
        }
      } catch (e) {}
      return dni;
    },
    // Lista ordenada estrictamente por orden de llegada / finalización en kiosko (FIFO)
    sortedReadyList() {
      if (!this.readyList || this.readyList.length === 0) return [];
      return [...this.readyList].sort((a, b) => {
        const timeA = new Date(a.CompletedAt || a.EnqueuedAt || 0).getTime();
        const timeB = new Date(b.CompletedAt || b.EnqueuedAt || 0).getTime();
        return timeA - timeB;
      });
    },
    nextWorkerShortName() {
      if (this.sortedReadyList.length === 0) return "";
      const fullName = this.sortedReadyList[0].FullName || "";
      const parts = fullName.trim().split(" ");
      return parts.slice(0, 2).join(" ");
    },
    filteredValidationList() {
      if (!this.validationList) return [];
      if (this.validationFilterTab === "MISSING_ADDRESS") {
        return this.validationList.filter((x) => !x.HasValidDniAddress && x.HasMedicalAptitude);
      }
      if (this.validationFilterTab === "MISSING_APTITUDE") {
        return this.validationList.filter((x) => !x.HasMedicalAptitude);
      }
      if (this.validationFilterTab === "READY") {
        return this.validationList.filter(
          (x) => x.ActionTaken === "ENCOLADO_EXITOSO" || x.ActionTaken === "YA_ENCOLADO"
        );
      }
      return this.validationList;
    },
  },
  mounted() {
    this.initSignalR();
    this.fetchQueueState();
    this.loadValidationStatus(false);
    this.pollTimer = setInterval(this.fetchQueueState, 3500);
    this.startLiveTimestampClock();
  },
  beforeDestroy() {
    if (this.pollTimer) clearInterval(this.pollTimer);
    if (this.timestampClockTimer) clearInterval(this.timestampClockTimer);
  },
  methods: {
    openValidationDialog() {
      this.validationDialog = true;
      this.loadValidationStatus(false);
    },
    async loadValidationStatus(runExecution = false) {
      this.loadingValidation = true;
      try {
        const userId =
          (this.$fun && this.$fun.getUserID && this.$fun.getUserID()) ||
          (this.$store && this.$store.getters ? this.$store.getters.getUserId || 1 : 1);
        const res = runExecution
          ? await ContractSignService.runValidationQueueJob(userId)
          : await ContractSignService.getValidationQueueStatus(userId);

        if (res && res.data) {
          const data = res.data;
          this.validationList = data.Details || [];
          this.validationSummary = {
            total: data.TotalEvaluated || 0,
            enqueued: data.EnqueuedCount || 0,
            missingAddress: data.MissingAddressCount || 0,
            missingAptitude: data.MissingAptitudeCount || 0,
            alreadyEnqueued: data.AlreadyEnqueuedCount || 0,
          };

          if (runExecution) {
            swal.fire({
              title: "Validación Ejecutada",
              text: `Evaluados: ${data.TotalEvaluated} | Encolados: ${data.EnqueuedCount} | Falta Dirección: ${data.MissingAddressCount} | Falta Aptitud: ${data.MissingAptitudeCount}`,
              icon: "info",
              timer: 3500,
            });
            this.fetchQueueState();
          }
        }
      } catch (err) {
        console.error("Error al cargar estado de validación:", err);
      } finally {
        this.loadingValidation = false;
      }
    },
    getActionColor(action) {
      switch (action) {
        case "ENCOLADO_EXITOSO":
          return "green";
        case "ALERTA_DIRECCION_FALTANTE":
          return "red";
        case "OMITIDO_SIN_APTITUD":
          return "orange darken-2";
        case "YA_ENCOLADO":
          return "blue";
        default:
          return "grey";
      }
    },
    getActionLabel(action) {
      switch (action) {
        case "ENCOLADO_EXITOSO":
          return "Encolado para Firma";
        case "ALERTA_DIRECCION_FALTANTE":
          return "Falta Dirección DNI";
        case "OMITIDO_SIN_APTITUD":
          return "Falta Aptitud Médica";
        case "YA_ENCOLADO":
          return "Ya en Cola Activa";
        default:
          return action || "Pendiente";
      }
    },
    getMessageClass(action) {
      switch (action) {
        case "ALERTA_DIRECCION_FALTANTE":
          return "red--text font-weight-medium";
        case "OMITIDO_SIN_APTITUD":
          return "orange--text text--darken-3 font-weight-medium";
        case "ENCOLADO_EXITOSO":
          return "green--text text--darken-2 font-weight-medium";
        default:
          return "grey--text text--darken-2";
      }
    },
    openAssignMedicalDialog(worker) {
      if (!worker) return;
      this.selectedWorkerForMedical = worker;
      this.assignMedicalDialog = true;
      this.loadWorkerMedicalList(worker.Dni, worker.AgtID);
    },

    async loadWorkerMedicalList(dni, agtID) {
      this.loadingWorkerMedicalList = true;
      this.workerMedicalList = [];
      try {
        const userId =
          (this.$fun && this.$fun.getUserID && this.$fun.getUserID()) || 1;
        const res = await ContractSignService.getWorkerMedicalEvaluations(
          dni,
          agtID,
          userId
        );
        if (res && res.data) {
          this.workerMedicalList = Array.isArray(res.data)
            ? res.data
            : res.data.data || [];
        }
      } catch (err) {
        console.error("Error al cargar historial médico del colaborador:", err);
        if (typeof swal !== "undefined" && swal.fire) {
          swal.fire("Error", "No se pudo cargar el historial médico.", "error");
        }
      } finally {
        this.loadingWorkerMedicalList = false;
      }
    },

    async confirmAssignMedical(evalItem) {
      if (!this.selectedWorkerForMedical || !evalItem) return;
      const worker = this.selectedWorkerForMedical;
      const agtId = worker.AgtID;
      const mevId = evalItem.MevID;

      const confirmMsg = `¿Desea vincular la evaluación médica del ${this.formatDisplayDate(
        evalItem.MevDate
      )} (${evalItem.MevStatusName || "Apto"}) al contrato de ${worker.FullName || worker.Dni}?`;

      let confirmed = false;
      if (typeof swal !== "undefined" && swal.fire) {
        const result = await swal.fire({
          title: "Vincular Aptitud Médica",
          text: confirmMsg,
          icon: "question",
          showCancelButton: true,
          confirmButtonText: "Sí, Vincular",
          cancelButtonText: "Cancelar",
          confirmButtonColor: "#1976D2",
        });
        confirmed = !!result.isConfirmed;
      } else {
        confirmed = window.confirm(confirmMsg);
      }

      if (!confirmed) return;

      this.assigningMedicalId = mevId;
      try {
        const userId =
          (this.$fun && this.$fun.getUserID && this.$fun.getUserID()) || 1;
        const res = await ContractSignService.assignMedicalEvaluation(
          {
            agtID: agtId,
            mevID: mevId,
          },
          userId
        );

        if (res && res.data && res.data.success) {
          if (typeof swal !== "undefined" && swal.fire) {
            swal.fire({
              title: "Aptitud Vinculada",
              text:
                res.data.message ||
                "Se vinculó la aptitud médica al contrato exitosamente.",
              icon: "success",
              timer: 3000,
            });
          }
          this.assignMedicalDialog = false;
          // Actualizar lista de validación y cola
          await this.loadValidationStatus(false);
          await this.fetchQueueState();
        } else {
          const msg =
            (res && res.data && res.data.message) ||
            "No se pudo vincular la evaluación médica.";
          if (typeof swal !== "undefined" && swal.fire) {
            swal.fire("Atención", msg, "warning");
          }
        }
      } catch (err) {
        console.error("Error al vincular aptitud médica:", err);
        if (typeof swal !== "undefined" && swal.fire) {
          swal.fire("Error", "Ocurrió un error al vincular la aptitud médica.", "error");
        }
      } finally {
        this.assigningMedicalId = null;
      }
    },

    async resendSignedEmail(target) {
      const agtId =
        (target && target.AgtID) ||
        (this.contractDetails && this.contractDetails.AgtID);
      const recipient =
        (target &&
          (target.WorkerEmail || target.Email || target.AgtEmailRecipient)) ||
        (this.contractDetails &&
          (this.contractDetails.WorkerEmail ||
            this.contractDetails.Email ||
            this.contractDetails.AgtEmailRecipient));
      const workerName =
        (target && target.FullName) ||
        (this.contractDetails && this.contractDetails.NtpFullName) ||
        "el colaborador";

      if (!agtId) {
        if (typeof swal !== "undefined" && swal.fire) {
          swal.fire(
            "Atención",
            "No se identificó el contrato para el envío de correo.",
            "warning"
          );
        }
        return;
      }

      let confirmed = false;
      const confirmText = recipient
        ? `¿Desea enviar/reenviar los documentos firmados por correo a ${recipient} (${workerName})?`
        : `¿Desea enviar los documentos firmados al correo registrado de ${workerName}?`;

      if (typeof swal !== "undefined" && swal.fire) {
        const result = await swal.fire({
          title: "Enviar Documentos Firmados",
          text: confirmText,
          icon: "question",
          showCancelButton: true,
          confirmButtonText: "Sí, Enviar Correo",
          cancelButtonText: "Cancelar",
          confirmButtonColor: "#1976D2",
        });
        confirmed = !!result.isConfirmed;
      } else {
        confirmed = window.confirm(confirmText);
      }

      if (!confirmed) return;

      this.resendingEmail = true;
      try {
        const userId =
          (this.$fun && this.$fun.getUserID && this.$fun.getUserID()) || 1;
        const res = await ContractSignService.resendSignedDocumentsEmail(
          agtId,
          recipient || null,
          userId
        );

        if (res && res.data && res.data.success) {
          if (typeof swal !== "undefined" && swal.fire) {
            swal.fire({
              title: "Correo Enviado",
              text:
                res.data.message ||
                "El correo con los documentos firmados se está procesando.",
              icon: "success",
              timer: 3500,
            });
          }
          if (target) {
            target.AgtIsEmailSent = 1;
            target.AgtEmailSentDate = new Date();
          }
          if (this.contractDetails) {
            this.contractDetails.AgtIsEmailSent = 1;
            this.contractDetails.AgtEmailSentDate = new Date();
          }
        } else {
          const msg =
            (res && res.data && res.data.message) ||
            "No se pudo procesar el envío de correo.";
          if (typeof swal !== "undefined" && swal.fire) {
            swal.fire("Atención", msg, "warning");
          }
        }
      } catch (err) {
        console.error("Error al reenviar correo de documentos firmados:", err);
        if (typeof swal !== "undefined" && swal.fire) {
          swal.fire(
            "Error",
            "Ocurrió un error al intentar enviar el correo.",
            "error"
          );
        }
      } finally {
        this.resendingEmail = false;
      }
    },

    formatDisplayDate(val) {
      if (!val) return "-";
      try {
        const d = new Date(val);
        if (isNaN(d.getTime())) return String(val).substring(0, 10);
        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
      } catch (e) {
        return String(val);
      }
    },
    initSignalR() {
      console.log("👤 [Operador RRHH] Conectando a SignalR...");
      DocumentSigningSignalrService.connectOperator({
        onQueueUpdated: (data) => {
          if (data) {
            if (data.ActiveKiosks) this.activeKiosks = data.ActiveKiosks;
            if (data.ReadyForOperator) this.readyList = data.ReadyForOperator;
            if (data.ActiveCalls) this.activeCallsList = data.ActiveCalls;
            if (data.WaitingQueue) this.waitingList = data.WaitingQueue;
          }
        },
        onTurnReadyForOperator: (turn) => {
          swal.fire({
            title: "¡Colaborador Listo para Firma!",
            text: `${turn.FullName} (${turn.Dni}) terminó de revisar sus documentos en el Terminal.`,
            icon: "success",
            timer: 4000,
          });
          this.fetchQueueState();
        },
      });
    },

    startLiveTimestampClock() {
      this.updateClockString();
      this.timestampClockTimer = setInterval(this.updateClockString, 1000);
    },

    updateClockString() {
      const now = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      this.currentTimestampString = `${pad(now.getDate())}/${pad(
        now.getMonth() + 1,
      )}/${now.getFullYear()} ${pad(now.getHours())}:${pad(
        now.getMinutes(),
      )}:${pad(now.getSeconds())}`;
    },

    async fetchQueueState() {
      try {
        const userId =
          (this.$fun && this.$fun.getUserID && this.$fun.getUserID()) || 1;
        const res = await ContractSignService.getQueueState(userId);
        if (res && res.data) {
          const data = res.data;
          this.activeKiosks = data.ActiveKiosks || [];
          this.readyList = data.ReadyForOperator || [];
          this.activeCallsList = data.ActiveCalls || [];
          this.waitingList = data.WaitingQueue || [];
        }
      } catch (e) {
        console.warn("👤 [Operador RRHH] Error en fetchQueueState:", e);
      }
    },

    async enqueueWorker() {
      if (!this.inputDni) return;
      this.enqueueLoading = true;
      this.enqueueError = "";
      this.enqueueSuccess = "";

      const dniClean = this.inputDni.trim();

      // Si el colaborador ya leyó y aceptó sus documentos, pasa directo a firma
      const alreadyReady = this.readyList.find(
        (x) => (x.Dni || x.dni || "").toString().trim() === dniClean,
      );
      if (alreadyReady) {
        this.enqueueSuccess = `¡El colaborador ${alreadyReady.FullName} ya completó la lectura en el Terminal (Turno ${alreadyReady.TurnCode})! Pasa directo a firma y huella.`;
        this.activeQueueTab = 0;
        this.openTurnDetails(alreadyReady);
        this.enqueueLoading = false;
        return;
      }

      // Si ya está siendo llamado o en lectura
      const alreadyInCall = this.activeCallsList.find(
        (x) => (x.Dni || x.dni || "").toString().trim() === dniClean,
      );
      if (alreadyInCall) {
        this.enqueueError = `El colaborador ya tiene turno en curso (${
          alreadyInCall.TurnCode
        }) en ${alreadyInCall.AssignedKiosk || "un terminal"}.`;
        this.enqueueLoading = false;
        return;
      }

      // Si ya está en espera
      const alreadyWaiting = this.waitingList.find(
        (x) => (x.Dni || x.dni || "").toString().trim() === dniClean,
      );
      if (alreadyWaiting) {
        this.enqueueError = `El colaborador ya se encuentra en cola de espera (Turno ${alreadyWaiting.TurnCode}).`;
        this.enqueueLoading = false;
        return;
      }

      try {
        const userId =
          (this.$fun && this.$fun.getUserID && this.$fun.getUserID()) || 1;
        const res = await ContractSignService.getPendingSignByDocument(
          dniClean,
          userId,
        );
        const contract = res && res.data;

        if (!contract || !contract.AgtID) {
          this.enqueueError =
            "No se encontró ningún contrato pendiente para el DNI ingresado.";
          this.enqueueLoading = false;
          return;
        }

        const turnPayload = {
          Dni: this.inputDni.trim(),
          FullName: contract.NtpFullName || "COLABORADOR",
          AgtID: contract.AgtID,
          PstID: contract.PstID || 0,
          PositionName: contract.PstName || "Operario",
          TypeContract: contract.TypeContract || 11,
          HasMedicalAptitude: true,
        };

        await ContractSignService.enqueueTurn(turnPayload, userId);
        this.enqueueSuccess = `¡Colaborador ${turnPayload.FullName} encolado exitosamente!`;
        this.inputDni = "";
        this.fetchQueueState();
      } catch (err) {
        this.enqueueError =
          (err && err.response && err.response.data) ||
          err.message ||
          "Error al encolar al colaborador.";
      } finally {
        this.enqueueLoading = false;
      }
    },

    callNextInQueue() {
      if (this.sortedReadyList.length > 0) {
        this.openTurnDetails(this.sortedReadyList[0]);
      }
    },

    async loadFullContractData(turn) {
      if (!turn) return null;
      const userId =
        (this.$fun && this.$fun.getUserID && this.$fun.getUserID()) || 1;
      const dni = (turn.Dni || turn.dni || "").toString().trim();
      let effectiveAgtId = Number(
        turn.AgtID ||
          turn.agtID ||
          turn.AgreementId ||
          turn.agreementId ||
          (turn.BatchPayload &&
            (turn.BatchPayload.AgtID || turn.BatchPayload.agtID)) ||
          0
      );

      // 1. Si no se tiene AgtID válido, intentar obtenerlo por el DNI pendiente
      if ((!effectiveAgtId || effectiveAgtId <= 0) && dni) {
        try {
          const resPending = await ContractSignService.getPendingSignByDocument(
            dni,
            userId,
          );
          if (resPending && resPending.data && (resPending.data.AgtID || resPending.data.agtID)) {
            effectiveAgtId = Number(resPending.data.AgtID || resPending.data.agtID);
          }
        } catch (e) {
          console.warn("No se pudo obtener AgtID por DNI:", e);
        }
      }

      let contractObj = {};
      // 2. Obtener los datos completos del contrato por su AgtID (incluye PadAddress, DisName, ProName, DepName, AreName)
      if (effectiveAgtId) {
        try {
          const res = await ContractSignService.getContractById(
            effectiveAgtId,
            userId,
            true,
          );
          if (res && res.data) {
            contractObj = Array.isArray(res.data)
              ? res.data[0] || {}
              : res.data || {};
          }
        } catch (e) {
          console.warn("Error cargando contrato por AgtID:", e);
        }
      }

      // Si itemsFunciones viene como string JSON, parsearlo correctamente
      if (typeof contractObj.itemsFunciones === "string") {
        try {
          contractObj.itemsFunciones = JSON.parse(contractObj.itemsFunciones);
        } catch (e) {
          console.error("Error al parsear itemsFunciones:", e);
        }
      }

      // Obtener el ID del puesto (PstID)
      const effectivePstId =
        contractObj.PstID ||
        turn.PstID ||
        turn.pstID ||
        (turn.BatchPayload &&
          (turn.BatchPayload.PstID || turn.BatchPayload.pstID)) ||
        0;

      // Cargar las funciones del puesto desde el servicio de puestos si no están presentes
      if (
        effectivePstId &&
        (!contractObj.itemsFunciones || !contractObj.itemsFunciones.length)
      ) {
        try {
          const resFunc = await _sPositionService.fuctionsPrimary(
            effectivePstId,
          );
          if (resFunc && resFunc.data) {
            contractObj.itemsFunciones = resFunc.data;
          }
        } catch (errFunc) {
          console.warn("Error cargando funciones del puesto:", errFunc);
        }
      }

      // 3. Fallback de seguridad: si PadAddress o AreName vienen vacíos en el contrato, consultar la persona natural por DNI
      if ((!contractObj.PadAddress || !contractObj.AreName) && dni) {
        try {
          const resPerson = await _sPersonService.getNaturalPerson(dni, userId);
          if (resPerson && resPerson.data) {
            const pData = resPerson.data;
            const genPerson = pData.GenPerson || {};
            const addresses = genPerson.Addresses || [];
            const defaultAddress =
              addresses.find((a) => a.PadIsDefault === 1) || addresses[0] || {};

            if (!contractObj.PadAddress) {
              contractObj.PadAddress =
                defaultAddress.PadAddress || genPerson.PrsAddress || "";
            }
            if (!contractObj.DisName && defaultAddress.DisName) {
              contractObj.DisName = defaultAddress.DisName;
            }
            if (!contractObj.ProName && defaultAddress.ProName) {
              contractObj.ProName = defaultAddress.ProName;
            }
            if (!contractObj.DepName && defaultAddress.DepName) {
              contractObj.DepName = defaultAddress.DepName;
            }
            if (!contractObj.AreName && genPerson.AreName) {
              contractObj.AreName = genPerson.AreName;
            }
            if (!contractObj.PstName && genPerson.PstName) {
              contractObj.PstName = genPerson.PstName;
            }
          }
        } catch (errPerson) {
          console.warn("Error en fallback de persona natural:", errPerson);
        }
      }

      // 4. Fusionar y asegurar que los campos requeridos por todos los formatos siempre tengan valor
      const merged = {
        ...turn,
        ...contractObj,
        AgtID: effectiveAgtId || turn.AgtID || turn.agtID || 0,
        PstID: effectivePstId,
        itemsFunciones: contractObj.itemsFunciones || turn.itemsFunciones || [],
        NtpFullName:
          contractObj.NtpFullName || turn.FullName || turn.fullName || "",
        PrsDocumentNumber:
          contractObj.PrsDocumentNumber || turn.Dni || turn.dni || dni,
        PadAddress: contractObj.PadAddress || "",
        DisName: contractObj.DisName || "",
        ProName: contractObj.ProName || "",
        DepName: contractObj.DepName || "",
        AreName:
          contractObj.AreName ||
          contractObj.AreNameJefatura ||
          contractObj.AreNameGerencia ||
          turn.AreName ||
          turn.PositionName ||
          "",
        AreNameJefatura:
          contractObj.AreNameJefatura ||
          contractObj.AreName ||
          turn.AreName ||
          "",
        AreNameGerencia: contractObj.AreNameGerencia || "PRODUCCION",
        PstName:
          contractObj.PstName || turn.PositionName || turn.positionName || "",
        TypeContract:
          contractObj.TypeContract ||
          turn.TypeContract ||
          turn.typeContract ||
          11,
      };

      this.contractDetails = merged;
      return merged;
    },

    clearSignature() {
      this.signatureBase64 = null;
      if (
        this.$refs.signaturePadRef &&
        typeof this.$refs.signaturePadRef.clearCanvas === "function"
      ) {
        this.$refs.signaturePadRef.clearCanvas();
      }
    },

    openDniModal() {
      this.showDniModal = true;
    },

    onDniSaved(payload) {
      if (payload) {
        this.dniFrontBase64 = payload.frontImage || this.dniFrontBase64;
        this.dniBackBase64 = payload.backImage || this.dniBackBase64;
        this.hasDniPhotos = !!(this.dniFrontBase64 || this.dniBackBase64);
      }
    },

    async openTurnDetails(turn) {
      this.selectedTurn = turn;
      this.signatureBase64 = null;
      this.fingerprintBase64 = null;
      this.dniFrontBase64 = null;
      this.dniBackBase64 = null;
      this.hasDniPhotos = false;
      this.signedTimestampValue = new Date();
      this.modalStep = 0;
      this.showDetailsModal = true;

      this.clearSignature();

      await this.loadFullContractData(turn);

      this.$nextTick(() => {
        this.initFingerprintCanvas();
      });
    },

    onSignatureSaved(base64Image) {
      this.signatureBase64 = base64Image;
    },

    onSignatureChanged(base64Image) {
      this.signatureBase64 = base64Image;
    },

    onFingerprintSaved(base64Image) {
      this.fingerprintBase64 = base64Image;
    },

    // Métodos para Canvas de Huella Digital
    initFingerprintCanvas() {
      const canvas = this.$refs.fingerprintCanvas;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = "#1e293b";
      ctx.lineWidth = 3;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
    },

    startFingerprintDraw(e) {
      this.isFingerprintDrawing = true;
      const canvas = this.$refs.fingerprintCanvas;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      const rect = canvas.getBoundingClientRect();
      ctx.beginPath();
      ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
    },

    drawFingerprint(e) {
      if (!this.isFingerprintDrawing) return;
      const canvas = this.$refs.fingerprintCanvas;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      const rect = canvas.getBoundingClientRect();
      ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
      ctx.stroke();
    },

    endFingerprintDraw() {
      if (this.isFingerprintDrawing) {
        this.isFingerprintDrawing = false;
        this.saveFingerprintFromCanvas();
      }
    },

    clearFingerprintCanvas() {
      this.fingerprintBase64 = null;
      this.$nextTick(() => {
        this.initFingerprintCanvas();
      });
    },

    saveFingerprintFromCanvas() {
      const canvas = this.$refs.fingerprintCanvas;
      if (canvas && !this.fingerprintBase64) {
        this.fingerprintBase64 = canvas.toDataURL("image/png");
      }
    },

    async confirmAndFinalizeContract() {
      if (!this.selectedTurn) return;

      // Asegurar que contractDetails esté completamente cargado con dirección y área
      if (!this.contractDetails || !this.contractDetails.PadAddress) {
        await this.loadFullContractData(this.selectedTurn);
      }

      // Obtener la firma del pad si aún no se guardó explícitamente
      if (!this.signatureBase64 && this.$refs.signaturePadRef) {
        try {
          if (typeof this.$refs.signaturePadRef.emitImage === "function") {
            this.$refs.signaturePadRef.emitImage();
          }
        } catch (e) {}
      }

      if (!this.signatureBase64) {
        swal.fire({
          title: "Firma Requerida",
          text: "Por favor capture la firma del colaborador (manual o Wacom) antes de finalizar.",
          icon: "warning",
        });
        return;
      }

      if (!this.fingerprintBase64) {
        const confirmWithoutFinger = await swal.fire({
          title: "¿Finalizar sin huella digital?",
          text: "No se ha capturado la huella digital del colaborador. Se recomienda registrar la huella para que todos los documentos la incluyan. ¿Desea continuar de todos modos?",
          icon: "question",
          showCancelButton: true,
          confirmButtonText: "Sí, finalizar",
          cancelButtonText: "No, capturar huella",
          confirmButtonColor: "#2e7d32",
        });
        if (!confirmWithoutFinger.isConfirmed) {
          return;
        }
      }

      this.signedTimestampValue = new Date();
      this.isFinalizing = true;

      try {
        const userId =
          (this.$fun && this.$fun.getUserID && this.$fun.getUserID()) || 1;

        // Esperar un ciclo de render para que los componentes tengan la firma, huella y timestamp actualizados
        await new Promise((resolve) => setTimeout(resolve, 350));

        // 1. Identificar únicamente los documentos que el trabajador leyó y aceptó
        let acceptedDocIdentifiers = [];

        let auditObj = this.selectedTurn.AuditLog;
        if (!auditObj && this.selectedTurn.AuditLogJson) {
          try {
            auditObj =
              typeof this.selectedTurn.AuditLogJson === "string"
                ? JSON.parse(this.selectedTurn.AuditLogJson)
                : this.selectedTurn.AuditLogJson;
          } catch (e) {}
        }

        if (
          auditObj &&
          Array.isArray(auditObj.Documents) &&
          auditObj.Documents.length > 0
        ) {
          acceptedDocIdentifiers = auditObj.Documents.filter(
            (d) => d.AcceptedTermsAt || d.ViewedAt || d.IsRequired,
          ).map((d) => {
            const comp = (d.ComponentName || "").toLowerCase().trim();
            const title = (d.Title || "").toLowerCase().trim();
            const type = (d.DocumentType || "").toLowerCase().trim();
            return `${comp} ${title} ${type}`;
          });
        } else {
          // Fallback a BatchPayload si no hay log de auditoría completo
          let batchObj = this.selectedTurn.BatchPayload;
          if (!batchObj && this.selectedTurn.BatchPayloadJson) {
            try {
              batchObj =
                typeof this.selectedTurn.BatchPayloadJson === "string"
                  ? JSON.parse(this.selectedTurn.BatchPayloadJson)
                  : this.selectedTurn.BatchPayloadJson;
            } catch (e) {}
          }
          if (batchObj && Array.isArray(batchObj.Documents)) {
            acceptedDocIdentifiers = batchObj.Documents.map((d) => {
              const comp = (d.ComponentName || "").toLowerCase().trim();
              const title = (d.Title || "").toLowerCase().trim();
              const type = (d.DocumentType || "").toLowerCase().trim();
              return `${comp} ${title} ${type}`;
            });
          }
        }

        const dni =
          (this.contractDetails && this.contractDetails.PrsDocumentNumber) ||
          this.selectedTurn.Dni ||
          this.selectedTurn.dni ||
          "";

        // Definición de todos los posibles formatos y su coincidencia con los documentos del trabajador
        const allAvailableDocDefs = [
          {
            ref: "pdfContractDoc",
            name: `Contrato_${dni}.pdf`,
            type: "CONTRATO",
            matches: (list) =>
              list.length === 0 ||
              list.some(
                (s) => s.includes("pdfcontract") || s.includes("contrato"),
              ),
          },
          {
            ref: "pdfAddressDoc",
            name: `DJ_Domicilio_${dni}.pdf`,
            type: "DJ_DOMICILIO",
            matches: (list) =>
              list.some(
                (s) =>
                  s.includes("address") ||
                  s.includes("domicilio") ||
                  s.includes("direccion"),
              ),
          },
          {
            ref: "pdfBackgroundDoc",
            name: `DJ_Antecedentes_${dni}.pdf`,
            type: "DJ_ANTECEDENTES",
            matches: (list) =>
              list.some(
                (s) => s.includes("background") || s.includes("antecedente"),
              ),
          },
          {
            ref: "pdfCodeOfConductDoc",
            name: `Codigo_Conducta_${dni}.pdf`,
            type: "CODIGO_CONDUCTA",
            matches: (list) =>
              list.some(
                (s) => s.includes("codeofconduct") || s.includes("conducta"),
              ),
          },
          {
            ref: "pdfDiningDoc",
            name: `Descuento_Comedor_${dni}.pdf`,
            type: "DESCUENTO_COMEDOR",
            matches: (list) =>
              list.some((s) => s.includes("dining") || s.includes("comedor")),
          },
          {
            ref: "pdfRisstDoc",
            name: `RISST_${dni}.pdf`,
            type: "RISST",
            matches: (list) => list.some((s) => s.includes("risst")),
          },
          {
            ref: "pdfRitDoc",
            name: `RIT_${dni}.pdf`,
            type: "RIT",
            matches: (list) =>
              list.some(
                (s) =>
                  s.includes("rit") ||
                  s.includes("reglamento interno de trabajo"),
              ),
          },
          {
            ref: "pdfSubstanceDoc",
            name: `DJ_Sustancias_${dni}.pdf`,
            type: "DJ_SUSTANCIAS",
            matches: (list) =>
              list.some(
                (s) => s.includes("substance") || s.includes("sustancia"),
              ),
          },
          {
            ref: "pdfAriDoc",
            name: `Consentimiento_ARI_${dni}.pdf`,
            type: "CONSENTIMIENTO_DIGITAL_ARI",
            matches: (list) =>
              list.some(
                (s) =>
                  s.includes("ari") ||
                  s.includes("boletas") ||
                  s.includes("digital consent") ||
                  s.includes("consentimiento") ||
                  s.includes("formatdigitalconsentari"),
              ),
          },
          {
            ref: "pdfAriDeliverableDoc",
            name: `Entregable_ARI_${dni}.pdf`,
            type: "ARI_ENTREGABLE",
            matches: (list) =>
              list.some(
                (s) =>
                  s.includes("entregable") ||
                  s.includes("formatdigitalconsentarideliverable"),
              ),
          },
          {
            ref: "pdfSstCriticalDoc",
            name: `Recomendaciones_SST_${dni}.pdf`,
            type: "FORMATSSTCRITICAL",
            matches: (list) =>
              list.some(
                (s) =>
                  s.includes("sst") ||
                  s.includes("iperc") ||
                  s.includes("formatsstcritical") ||
                  s.includes("peligro") ||
                  s.includes("riesgo") ||
                  s.includes("matriz"),
              ),
          },
          {
            ref: "pdfOvertimeDoc",
            name: `Convenio_HorasExtras_${dni}.pdf`,
            type: "CONVENIO_HORAS_EXTRAS",
            matches: (list) =>
              list.some(
                (s) =>
                  s.includes("overtime") ||
                  s.includes("horas") ||
                  s.includes("compensacion") ||
                  s.includes("formatovertimecompensation"),
              ),
          },
        ];

        // 1. Extraer los identificadores de documentos aceptados
        const acceptedDocuments = ["CONTRATO"];
        if (
          acceptedDocIdentifiers.some(
            (s) => s.includes("domicilio") || s.includes("address") || s.includes("formataddressdeclaration"),
          )
        ) {
          acceptedDocuments.push("DJ_DOMICILIO");
        }
        if (
          acceptedDocIdentifiers.some(
            (s) => s.includes("antecedente") || s.includes("background") || s.includes("formatbackgrounddeclaration"),
          )
        ) {
          acceptedDocuments.push("DJ_ANTECEDENTES");
        }
        if (
          acceptedDocIdentifiers.some(
            (s) => s.includes("conducta") || s.includes("conduct") || s.includes("formatcodeofconduct"),
          )
        ) {
          acceptedDocuments.push("CODIGO_CONDUCTA");
        }
        if (
          acceptedDocIdentifiers.some(
            (s) => s.includes("comedor") || s.includes("dining") || s.includes("formatdiningdeduction"),
          )
        ) {
          acceptedDocuments.push("DESCUENTO_COMEDOR");
        }
        if (
          acceptedDocIdentifiers.some(
            (s) => s.includes("risst") || s.includes("formatrisst") || s.includes("seguridad"),
          )
        ) {
          acceptedDocuments.push("RISST");
        }
        if (
          acceptedDocIdentifiers.some(
            (s) =>
              s.includes("rit") || s.includes("formatrit") || s.includes("reglamento interno de trabajo"),
          )
        ) {
          acceptedDocuments.push("RIT");
        }
        if (
          acceptedDocIdentifiers.some(
            (s) => s.includes("substance") || s.includes("sustancia") || s.includes("formatsubstancedeclaration"),
          )
        ) {
          acceptedDocuments.push("DJ_SUSTANCIAS");
        }
        if (
          acceptedDocIdentifiers.some(
            (s) =>
              s.includes("overtime") ||
              s.includes("horas") ||
              s.includes("compensacion") ||
              s.includes("formatovertimecompensation"),
          )
        ) {
          acceptedDocuments.push("CONVENIO_HORAS_EXTRAS");
        }
        if (
          acceptedDocIdentifiers.some(
            (s) =>
              s.includes("ari") ||
              s.includes("boletas") ||
              s.includes("digital consent") ||
              s.includes("consentimiento") ||
              s.includes("formatdigitalconsentari"),
          )
        ) {
          acceptedDocuments.push("CONSENTIMIENTO_DIGITAL_ARI");
        }
        if (
          acceptedDocIdentifiers.some(
            (s) =>
              s.includes("entregable") ||
              s.includes("formatdigitalconsentarideliverable"),
          )
        ) {
          acceptedDocuments.push("ARI_ENTREGABLE");
        }
        if (
          acceptedDocIdentifiers.some(
            (s) =>
              s.includes("sst") ||
              s.includes("iperc") ||
              s.includes("peligro") ||
              s.includes("riesgo") ||
              s.includes("formatsstcritical") ||
              s.includes("matriz"),
          )
        ) {
          acceptedDocuments.push("FORMATSSTCRITICAL");
        }
        if (
          acceptedDocIdentifiers.some(
            (s) =>
              s.includes("aptitud") ||
              s.includes("aptitude") ||
              s.includes("medica") ||
              s.includes("formatmedicalaptitude"),
          )
        ) {
          acceptedDocuments.push("APTITUD_MEDICA");
        }

        console.log(
          `📄 [Backend PDF Generator] Encolando ${acceptedDocuments.length} documentos para DNI ${dni}:`,
          acceptedDocuments,
        );

        // 2. Preparar payload con Auditoría de lectura
        const auditLogPayload = this.selectedTurn.AuditLog
          ? typeof this.selectedTurn.AuditLog === "object"
            ? JSON.stringify(this.selectedTurn.AuditLog)
            : this.selectedTurn.AuditLog
          : this.selectedTurn.AuditLogJson;

        const agtId =
          (this.contractDetails && this.contractDetails.AgtID) ||
          this.selectedTurn.AgtID ||
          this.selectedTurn.agtID ||
          0;
        const fullName =
          (this.contractDetails && this.contractDetails.NtpFullName) ||
          this.selectedTurn.FullName ||
          this.selectedTurn.fullName ||
          "";

        // Delegar la compilación oficial de alta fidelidad (vectorial, exacta de 2 páginas, sin marcas amarillas)
        // directamente al motor Chromium del backend (ContractPdfGeneratorService)
        const filesToUpload = [];

        const payload = {
          TurnId: this.selectedTurn.TurnId || this.selectedTurn.turnId,
          AgtID: agtId,
          Dni: dni,
          FullName: fullName,
          SignatureBase64: this.signatureBase64,
          FingerprintBase64: this.fingerprintBase64,
          DniFrontBase64: this.dniFrontBase64,
          DniBackBase64: this.dniBackBase64,
          AuditLogJson: auditLogPayload,
          SignedAt: this.signedTimestampValue || new Date().toISOString(),
          AcceptedDocuments: acceptedDocuments,
          Files: filesToUpload,
        };

        const res = await ContractSignService.finishSignedContract(
          payload,
          userId,
        );

        if (res && res.data && res.data.success) {
          swal.fire({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 4000,
            timerProgressBar: true,
            icon: "success",
            title: `Contrato de ${
              fullName || dni
            } firmado. Generando PDFs en backend...`,
          });
        }

        const turnId = this.selectedTurn.TurnId || this.selectedTurn.turnId;
        const assignedKiosk =
          this.selectedTurn.AssignedKiosk || this.selectedTurn.assignedKiosk;

        // Liberar terminal y finalizar turno a través de SignalR
        if (turnId) {
          DocumentSigningSignalrService.operatorFinishTurn(turnId);
        }
        if (assignedKiosk) {
          DocumentSigningSignalrService.releaseKiosk(assignedKiosk);
        }

        this.showDetailsModal = false;
        this.selectedTurn = null;
        this.contractDetails = null;
        this.signatureBase64 = null;
        this.fingerprintBase64 = null;
        this.fetchQueueState();
      } catch (e) {
        console.error("Error al finalizar contrato:", e);
        swal.fire({
          title: "Error al finalizar",
          text:
            (e && e.response && e.response.data) ||
            e.message ||
            "Ocurrió un error guardando el contrato.",
          icon: "error",
        });
      } finally {
        this.isFinalizing = false;
      }
    },

    async manualReleaseTurnKiosk(turn) {
      if (!turn) return;
      const kioskId = turn.AssignedKiosk || turn.assignedKiosk;
      if (!kioskId) return;

      const confirm = await swal.fire({
        title: "¿Liberar Terminal?",
        text: `¿Desea liberar la estación "${kioskId}"? La tablet volverá al estado libre para que otro colaborador en cola pueda revisar sus documentos. El colaborador ${
          turn.FullName || "actual"
        } permanecerá en la lista de atención listo para firmar con el operador.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Sí, liberar terminal",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#e65100",
      });

      if (!confirm.isConfirmed) return;

      try {
        const userId =
          (this.$fun && this.$fun.getUserID && this.$fun.getUserID()) || 1;

        // Liberar terminal tanto por SignalR como por API HTTP para máxima confiabilidad
        DocumentSigningSignalrService.releaseKiosk(kioskId);
        await ContractSignService.releaseKiosk(kioskId, userId).catch(() => {});

        // Desvincular localmente para reflejar de inmediato en la vista sin perder el turno
        this.$set(turn, "AssignedKiosk", null);
        if (this.selectedTurn && this.selectedTurn.TurnId === turn.TurnId) {
          this.$set(this.selectedTurn, "AssignedKiosk", null);
        }

        await this.fetchQueueState();
        swal.fire({
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3500,
          icon: "success",
          title: `Terminal ${kioskId} liberado. El colaborador continúa listo en la lista para firma.`,
        });
      } catch (e) {
        console.warn("Error al liberar terminal:", e);
      }
    },

    async cancelReadyTurn(turn) {
      if (!turn) return;
      const turnId = turn.TurnId || turn.turnId;
      const kioskId = turn.AssignedKiosk || turn.assignedKiosk;

      const confirm = await swal.fire({
        title: "¿Descartar o Cancelar Turno?",
        text: `¿Desea retirar de la cola a ${
          turn.FullName || "este colaborador"
        }? Utilice esto solo si el colaborador se retiró o no completará la firma.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, descartar turno",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#d32f2f",
      });

      if (!confirm.isConfirmed) return;

      try {
        const userId =
          (this.$fun && this.$fun.getUserID && this.$fun.getUserID()) || 1;
        if (turnId) {
          await ContractSignService.cancelTurn(turnId, userId);
        }
        if (kioskId) {
          DocumentSigningSignalrService.releaseKiosk(kioskId);
        }
        await this.fetchQueueState();
        swal.fire({
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
          icon: "info",
          title: "Turno retirado de la cola de atención.",
        });
      } catch (e) {
        console.warn("Error al cancelar turno:", e);
      }
    },

    async renderElementToPdfBase64(element) {
      if (!element) return null;

      // Buscar el contenedor real del documento, omitiendo toolbars o botones de interfaz
      const target =
        element.querySelector(".contract-html-page") ||
        element.querySelector(".pdf-item") ||
        element.querySelector("section[slot='pdf-content']") ||
        element.querySelector(".format-document") ||
        element;

      // Ocultar temporalmente toolbars o botones dentro del elemento si existieran
      const uiElements = element.querySelectorAll(
        ".v-toolbar, .s-toolbar, button, .v-btn, hr",
      );
      uiElements.forEach((el) => (el.style.visibility = "hidden"));

      // Desactivar restricciones de altura y scroll para capturar el 100% de las páginas
      const scrollContainers = element.querySelectorAll(
        ".contract-viewer-body, .contract-card-viewer, .v-card__text",
      );
      const originalStyles = [];
      scrollContainers.forEach((sc) => {
        originalStyles.push({
          el: sc,
          maxHeight: sc.style.maxHeight,
          overflow: sc.style.overflow,
          height: sc.style.height,
        });
        sc.style.maxHeight = "none";
        sc.style.overflow = "visible";
        sc.style.height = "auto";
      });

      try {
        const canvas = await html2canvas(target, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
        });

        const pdf = new jsPDF("p", "mm", "a4");
        const pageWidth = 210;
        const pageHeight = 297;

        // Márgenes legales seguros: 15mm arriba, 15mm abajo, 15mm izquierda y derecha
        const marginLeft = 5;
        const marginRight = 5;
        const marginTop = 5;
        const marginBottom = 5;

        const printableWidth = pageWidth - marginLeft - marginRight; // 180 mm
        const printableHeight = pageHeight - marginTop - marginBottom; // 267 mm

        const pxPerMm = canvas.width / printableWidth;
        const maxSliceHeightPx = Math.floor(printableHeight * pxPerMm);

        const ctx = canvas.getContext("2d");
        let currentY = 0;
        let pageIndex = 0;

        while (currentY < canvas.height) {
          const remainingHeight = canvas.height - currentY;
          let sliceHeight = Math.min(maxSliceHeightPx, remainingHeight);

          // Si el contenido restante excede una página, buscar una fila en blanco
          // para no cortar texto ni cláusulas a la mitad horizontalmente
          if (remainingHeight > maxSliceHeightPx) {
            const searchRange = Math.min(
              220,
              Math.floor(maxSliceHeightPx * 0.25),
            );
            const startScanY = currentY + maxSliceHeightPx - searchRange;

            try {
              const imgData = ctx.getImageData(
                0,
                startScanY,
                canvas.width,
                searchRange,
              );
              const data = imgData.data;

              let bestYOffset = -1;
              const xStart = Math.floor(canvas.width * 0.08);
              const xEnd = Math.floor(canvas.width * 0.92);
              const step = 4;

              // Buscar de abajo hacia arriba la primera fila que sea completamente blanca (vacía)
              for (let y = searchRange - 1; y >= 0; y--) {
                let isRowBlank = true;
                for (let x = xStart; x < xEnd; x += step) {
                  const idx = (y * canvas.width + x) * 4;
                  const r = data[idx];
                  const g = data[idx + 1];
                  const b = data[idx + 2];
                  // Si algún píxel no es blanco, hay texto en esta fila
                  if (r < 235 || g < 235 || b < 235) {
                    isRowBlank = false;
                    break;
                  }
                }
                if (isRowBlank) {
                  bestYOffset = y;
                  break;
                }
              }

              if (bestYOffset >= 0) {
                sliceHeight =
                  maxSliceHeightPx - (searchRange - 1 - bestYOffset);
              }
            } catch (scanErr) {
              // En caso de excepción al leer ImageData, usar corte estándar seguro
            }
          }

          // Crear canvas de la página individual recortada
          const pageCanvas = document.createElement("canvas");
          pageCanvas.width = canvas.width;
          pageCanvas.height = sliceHeight;

          const pageCtx = pageCanvas.getContext("2d");
          pageCtx.fillStyle = "#ffffff";
          pageCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
          pageCtx.drawImage(
            canvas,
            0,
            currentY,
            canvas.width,
            sliceHeight,
            0,
            0,
            canvas.width,
            sliceHeight,
          );

          const sliceHeightMm = sliceHeight / pxPerMm;

          if (pageIndex > 0) {
            pdf.addPage();
          }

          pdf.addImage(
            pageCanvas.toDataURL("image/jpeg", 0.95),
            "JPEG",
            marginLeft,
            marginTop,
            printableWidth,
            sliceHeightMm,
          );

          currentY += sliceHeight;
          pageIndex++;
        }

        return pdf.output("datauristring");
      } finally {
        uiElements.forEach((el) => (el.style.visibility = ""));
        originalStyles.forEach((item) => {
          item.el.style.maxHeight = item.maxHeight;
          item.el.style.overflow = item.overflow;
          item.el.style.height = item.height;
        });
      }
    },

    async cancelOrReleaseStuckTurn(item) {
      if (!item) return;
      const turnId = item.TurnId;
      const kiosk = item.AssignedKiosk;
      const userId =
        (this.$fun && this.$fun.getUserID && this.$fun.getUserID()) || 1;

      try {
        if (turnId) {
          await ContractSignService.cancelTurn(turnId, userId);
        }
        if (this.$toastr && this.$toastr.success) {
          this.$toastr.success(
            `Turno cancelado y ${
              kiosk || "Terminal"
            } liberado para el siguiente colaborador.`,
          );
        }
        this.fetchQueueState();
      } catch (e) {
        console.warn("Error cancelando turno:", e);
      }
    },

    timeSince(dateVal) {
      if (!dateVal) return "Recién";
      const d = new Date(dateVal);
      if (isNaN(d.getTime())) return "";
      const seconds = Math.floor((new Date() - d) / 1000);
      if (seconds < 60) return `${Math.max(1, seconds)} seg`;
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes} min`;
      const hours = Math.floor(minutes / 60);
      return `${hours} h`;
    },

    formatTimeOnly(val) {
      if (!val) return "";
      const d = new Date(val);
      if (isNaN(d.getTime())) return "";
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(
        d.getSeconds(),
      )}`;
    },

    formatDateTime(val) {
      if (!val) return "";
      if (this.$moment) {
        return this.$moment(val).format("YYYY-MM-DD HH:mm:ss");
      }
      const d = new Date(val);
      if (isNaN(d.getTime())) return String(val);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
        d.getDate(),
      )} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    },
  },
};
</script>

<style scoped>
.contract-sign-view {
  background: #f8fafc;
  min-height: 100vh;
}

.border-success {
  border: 2px solid #22c55e !important;
  background: #f0fdf4 !important;
}

.border-primary {
  border: 2px solid #2563eb !important;
  background: #eff6ff !important;
}

.border-blue {
  border: 1.5px solid #3b82f6 !important;
}

.font-mono {
  font-family: "Courier New", Courier, monospace;
}

.gap-2 {
  gap: 8px;
}

.fingerprint-canvas-wrapper {
  background: #ffffff;
  border: 2px dashed #94a3b8;
  border-radius: 8px;
  cursor: crosshair;
  touch-action: none;
}

.fingerprint-canvas {
  display: block;
}

.transition-fast {
  transition: all 0.2s ease-in-out;
}

.hidden-pdf-render-container >>> .contract-viewer-body {
  max-height: none !important;
  overflow: visible !important;
}

.hidden-pdf-render-container >>> .contract-card-viewer {
  max-width: none !important;
  width: 100% !important;
}

.hidden-pdf-render-container >>> .contract-html-page {
  max-width: none !important;
  width: 100% !important;
}
</style>
